import {
  ArrowLeft,
  Circle,
  Clock,
  Eye,
  Hand,
  Mic,
  MicOff,
  ShieldAlert,
  Video,
  VideoOff,
  X,
} from 'lucide-react';
import { AnimatePresence, motion } from 'framer-motion';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { JSX } from 'react';
import { useTranslation } from 'react-i18next';
import { Room, RoomEvent, Track } from 'livekit-client';
import type { RoomConnectOptions, RoomOptions } from 'livekit-client';

import { LangSwitch } from '@/components/LangSwitch';
import { Badge } from '@/components/ui/badge';
import { SignalBars } from '@/components/signal-bars';
import { cn } from '@/lib/utils';
import { navigateTo } from '@/lib/spa';

import { joinRoom, JoinApiError, remintToken as remintTokenApi } from '../api/join';
import type { GuestJoinBody } from '../api/join';
import type { JoinCredentials, TurnCredentials } from '../api/join';
import { grantBoard, revokeBoard } from '../api/board';
import {
  isActiveRecordingStatus,
  listRecordings,
  recordingErrorKind,
  startRecording,
  stopRecording,
} from '../api/recording';
import type { RecordingSnapshot } from '../api/recording';
import { fetchSpeakToken, grantSpeak, revokeSpeak } from '../api/speak';
import {
  browserSsoStorage,
  callWithSsoRecovery,
  clearSsoSession,
  ssoConfigFromEnv,
  startSsoAutoRefresh,
} from '../auth/sso';
import type {
  AssignableRole,
  LobbyAdmittedMedia,
  ModerationAction,
  ParticipantRole,
} from '../realtime/frames';
import { useRealtimeRoom } from '../realtime/useRealtimeRoom';
import type { RealtimeRoomActions } from '../realtime/useRealtimeRoom';
import { AudioSink } from './AudioSink';
import { BoardPanel } from './board/BoardPanel';
import type { BoardPointerBatch } from './board/CursorLayer';
import { BoardSyncHub } from './board/collab';
import type { BoardSync, BoardSyncElement } from './board/collab';
import { ControlBar } from './ControlBar';
import { EffectsDialog } from './EffectsDialog';
import { InviteDialog } from './InviteDialog';
import { InvitesDialog } from './InvitesDialog';
import { LobbyRejectedScreen, LobbyWaitingScreen } from './LobbyScreens';
import type { LobbyRejectionKind } from './LobbyScreens';
import { MeetingLayout } from './MeetingLayout';
import { EndedScreen } from './EndedScreen';
import { Prejoin } from './Prejoin';
import { ReactionOverlay, REACTION_FLOAT_MS } from './ReactionOverlay';
import type { ReactionFloater } from './ReactionOverlay';
import type { RecordingsPanelProps } from './RecordingsPanel';
import { RemovedScreen } from './RemovedScreen';
import { ShortcutsDialog } from './ShortcutsDialog';
import { SidePanel } from './SidePanel';
import { VideoTile } from './VideoTile';
import { WebinarControlBar } from './WebinarControlBar';
import { WebinarSidePanel } from './WebinarSidePanel';
import { WebinarStage } from './WebinarStage';
import { measureControlRtt } from './connectionCheck';
import { devicePrefsStore } from './devicePrefs';
import type { DevicePrefs } from './devicePrefs';
import {
  createCameraEffectsMachine,
  effectsRequestFromPrefs,
  exposeCameraFxForTests,
  requestsDiffer,
} from './cameraEffects';
import type {
  CameraEffectsMachine,
  CameraEffectsRequest,
  CameraPublicationHandle,
} from './cameraEffects';
import { startBackgroundProcessor, exposeFxPerfForTests } from '../media/backgroundProcessor';
import {
  applyMicEffectSettings,
  audioEffectSettingsFromPrefs,
  audioEffectSettingsWithDefaults,
  exposeMicSettingsForTests,
  microphoneCaptureOptions,
  prefsPatchForEffect,
  readAudioEffectSettings,
} from './micEffects';
import type { AudioEffectSettings } from './micEffects';
import { moderationErrorKind } from './moderation';
import { canModerate } from './moderation';
import { roomLink } from './prejoinState';
import type { BackgroundHandover, PrejoinJoinRequest } from './prejoinState';
import { useIsMobile } from './useIsMobile';
import { isViewerJoinToken, viewersCount, webinarStageTiles } from './webinar';
import {
  cameraCaptureOptions,
  cameraPublishOptions,
  decidePublishPath,
  detectHardwareSignal,
  detectVideoSendCapabilities,
  exposeCamStatsForTests,
  microphonePublishOptions,
  PUBLISH_TIER_CAPS,
  resolveSessionTier,
} from './publishQuality';
import type { PublishDecision, PublishMode, PublishTier } from './publishQuality';
import type { CameraQualitySetting } from './publishQuality';
import type { BackgroundMode } from '../media/backgroundProcessor';
import type { QuickSettingsBinding } from './ControlBar';
import { sessionEffectsActive } from './prejoinState';
import { exposeReceiverStatsForTests } from './subscribeQuality';
import type { ReceiverVideoStatSnapshot } from './subscribeQuality';
import {
  decideScreenSharePath,
  screenShareCaptureOptions,
  screenSharePublishOptions,
} from './screenShareQuality';
import {
  checkDisplayName,
  connectionPhaseFromRoomState,
  deriveScreenShares,
  deriveTiles,
  disconnectReasonKind,
  formatMeetingTimer,
  isScreenSharing,
  isUserInitiatedDisconnect,
  isValidRoomSlug,
  nextPanel,
  remoteAudioTracks,
  signalLevel,
  togglePinned,
} from './roomState';
import type { ConnectionPhase, DisconnectReasonKind, PanelMode } from './roomState';

type Phase = 'pre-join' | 'joining' | 'connected' | 'left' | 'dropped' | 'failed';

/**
 * Room construction options, shared by every media session of this page (direct join, lobby
 * admit, media handover reconnect). `pixelDensity: 'screen'` is the receiving-side fix of
 * the camera-quality slice: livekit-client's adaptive stream defaults the density to 1
 * (device pixels only when devicePixelRatio > 2), so on Retina Macs (DPR 2) and scaled
 * Windows displays (DPR 1.25-1.5) every subscription was sized in CSS pixels - the dominant
 * slot requested the 720p layer and grid tiles the 360p layer, and the 1080p publish was
 * consumed at its top layer NOWHERE. With 'screen' the element boxes are read in device
 * pixels; the rendered camera tiles additionally request explicit dimensions through
 * subscribeQuality.ts's ladder (dominant uncapped, grid tiles capped by tile count).
 */
const ROOM_OPTIONS: RoomOptions = { adaptiveStream: { pixelDensity: 'screen' }, dynacast: true };

interface RoomPageProps {
  readonly slug: string;
}

/** Verba conn-state chip colors: emerald connected, amber reconnecting, red disconnected. */
const CONN_CHIP_CLASSES: Readonly<Record<ConnectionPhase, string>> = {
  connected: 'border-emerald-500/40 bg-emerald-500/10 text-emerald-300',
  reconnecting: 'border-amber-500/40 bg-amber-500/10 text-amber-300',
  disconnected: 'border-red-500/40 bg-red-500/10 text-red-300',
};

const CONN_DOT_CLASSES: Readonly<Record<ConnectionPhase, string>> = {
  connected: 'bg-emerald-400',
  reconnecting: 'animate-pulse bg-amber-400',
  disconnected: 'bg-red-400',
};

/** How long the moderation toast stays visible. */
const TOAST_CLEAR_MS = 4000;
/** How long the hotkey feedback pill stays visible above the stage. */
const PILL_VISIBLE_MS = 1100;
/** Poll cadence of the header's control-plane RTT probe (the unified room header). */
const RTT_PROBE_INTERVAL_MS = 10_000;
/** Maximum floaters kept at once; older ones drop off the left of the list. */
const MAX_FLOATERS = 14;
/**
 * Backoff before the single 429 retry of the re-mint path: the server's per-identity remint
 * bucket is 5 per minute, so a drained bucket needs a moment, but the reconnect must not stall
 * for long while the socket sits unauthenticated.
 */
const REMINT_RETRY_DELAY_MS = 2000;
/**
 * How often the connected session re-checks the current join token's remaining life for the
 * proactive renewal (the client side of CONTROL_MEDIA_TOKEN_TTL, CR-13). The check also runs
 * when the tab becomes visible again: background throttling delays timers, and the catch-up
 * keeps the warm token valid for the next reconnect.
 */
const MEDIA_TOKEN_RENEW_CHECK_MS = 30_000;
/**
 * Delays (ms) between the automatic media-reconnect attempts that follow an unintended drop,
 * each attempt minting fresh credentials. The escalation rides out multi-minute network
 * outages (Wi-Fi switch, VPN blip) instead of exhausting in seconds; exhaustion lands on the
 * dropped screen exactly as before this mechanism. A browser 'online' event mid-outage cuts
 * the pending backoff slot short and retries at once (the fast-path recovery below).
 * Exported for the recovery tests, which walk the whole schedule.
 */
export const MEDIA_DROP_RECOVERY_DELAYS_MS: readonly number[] = [
  1_000, 2_000, 4_000, 8_000, 15_000, 30_000, 60_000, 120_000,
];
/**
 * How long the recovery may hold (polling navigator.onLine every second) instead of burning
 * attempts while the browser reports no connectivity: an offline attempt fails instantly, so
 * a drop that outlasts a network outage would otherwise exhaust into the dropped screen
 * before the network even returned. Past this cap the attempts proceed (and fail fast), so a
 * genuinely dead session still ends on the dropped screen.
 */
const MEDIA_RECOVERY_ONLINE_HOLD_MS = 90_000;
/** Poll cadence of the offline hold above. */
const MEDIA_RECOVERY_ONLINE_POLL_MS = 1_000;

/** Remaining-life window of the CURRENT renewable join token (the proactive-renewal input). */
interface TokenWindow {
  readonly mintedAt: number;
  readonly expiresAt: number;
}

/**
 * The validity window of a just-minted join response: minted now, expiring at the response's
 * expiresAt. Null when the response carries no parseable expiry (the field is required by the
 * contract, but a broken server must degrade to "no proactive renewal", not to a crash).
 */
function credentialsWindow(credentials: JoinCredentials): TokenWindow | null {
  return tokenWindowFromExpiresAt(credentials.expiresAt);
}

/** Same window for the admitted media credentials (CR-15), which ride the WS frame. */
function tokenWindowFromExpiresAt(expiresAt: string): TokenWindow | null {
  const parsed = Date.parse(expiresAt);
  if (!Number.isFinite(parsed)) {
    return null;
  }
  return { mintedAt: Date.now(), expiresAt: parsed };
}

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * i18n keys of the prejoin notices for the SSO join error codes (docs/contract.md M5): the
 * guest path stays usable in every case, and sso_invalid additionally drops the expired
 * session. Unknown codes surface no notice.
 */
function ssoJoinNoticeKey(
  code: string,
): 'sso.join_sso_invalid' | 'sso.join_sso_unavailable' | 'sso.join_sso_disabled' | null {
  switch (code) {
    case 'sso_invalid':
      return 'sso.join_sso_invalid';
    case 'sso_unavailable':
      return 'sso.join_sso_unavailable';
    case 'sso_disabled':
      return 'sso.join_sso_disabled';
    default:
      return null;
  }
}

/**
 * The room lifecycle outcome of one join failure (CR-26): the control service refuses unknown
 * rooms (404 room_not_found - the lazy create-by-join is gone) and expired ones (410
 * room_expired); each lands on its own screen instead of the generic failure alert. The WS
 * 4404/4410 closes surface through the same codes on a mid-room remint, which the reconnect
 * path reports as its own drop reason - this helper only classifies the REST join.
 */
function lifecycleStateOf(err: unknown): 'none' | 'room_not_found' | 'room_expired' {
  if (!(err instanceof JoinApiError)) {
    return 'none';
  }
  if (err.code === 'room_not_found') {
    return 'room_not_found';
  }
  if (err.code === 'room_expired') {
    return 'room_expired';
  }
  return 'none';
}

/** A publish failure of one media kind, kept as a stable kind plus the raw device detail. */
interface MediaWarning {
  readonly kind: 'camera' | 'microphone';
  readonly detail: string;
}

/**
 * The `?invite=` value of a deep link (CR-20): verbatim for the join body, null when absent,
 * blank or absurdly long (the real secret is 43 chars; the cap only stops junk URLs from
 * shipping multi-kilobyte bodies). Pure so tests can pin the parsing.
 */
export function parseInviteToken(search: string): string | null {
  const raw = new URLSearchParams(search).get('invite');
  if (raw === null || raw.length === 0 || raw.length > 512) {
    return null;
  }
  return raw;
}

/** The guest join body of the session: the display name plus the deep link's invitation, if any. */
function guestJoinBody(name: string, inviteToken: string | null): GuestJoinBody {
  return inviteToken !== null ? { displayName: name, inviteToken } : { displayName: name };
}

/** Which icon the hotkey feedback pill renders. */
type PillKind = 'mic-on' | 'mic-off' | 'cam-on' | 'cam-off' | 'hand';

/**
 * The /r/:room page, styled after the Verba conference room: a top bar (room slug, connection
 * state, participant count, publish path, meeting timer), a central video stage (screen shares
 * dominant, equal grid or speaker spotlight with filmstrip) and the floating control pill, plus
 * a collapsible right panel with the participants list and the chat placeholder. The prejoin
 * screen picks devices and a display name; joining mints a token through the control service
 * (same-origin /api), connects the LiveKit room and publishes the chosen devices. The room is
 * disconnected on unload and on unmount.
 */
export function RoomPage({ slug }: RoomPageProps): JSX.Element {
  const { t } = useTranslation();
  // Server-assigned participant identity from the initial join response (guest-<16 hex> for
  // guests, kc-<sub> for SSO): the authoritative own presence, kept in memory only - a reload
  // re-joins and the server mints a fresh one. Seeds the own-identity fallback until the
  // realtime state frame lands.
  const [identity, setIdentity] = useState<string | null>(null);
  const isMobile = useIsMobile();
  const [phase, setPhase] = useState<Phase>('pre-join');
  const [error, setError] = useState<string | null>(null);
  /**
   * The room lifecycle outcome of the join (CR-26): the control service refuses unknown rooms
   * (404 room_not_found - the lazy create-by-join is gone) and expired ones (410 room_expired),
   * and each lands on its own screen instead of the generic failure alert. 'none' = the join
   * failed (or succeeded) for any other reason.
   */
  const [lifecycleState, setLifecycleState] = useState<'none' | 'room_not_found' | 'room_expired'>(
    'none',
  );
  // Non-blocking SSO notice (sso_invalid / sso_unavailable / sso_disabled, session expiry);
  // shown in the prejoin card while the guest form stays usable.
  const [ssoNotice, setSsoNotice] = useState<string | null>(null);
  // SSO session state: the Keycloak access token the prejoin signed in with (mirrored from
  // sessionStorage at join time), and the role the join response resolved for the joiner.
  const [ssoToken, setSsoToken] = useState<string | null>(null);
  const [joinedRole, setJoinedRole] = useState<ParticipantRole>('participant');
  const [mediaWarnings, setMediaWarnings] = useState<readonly MediaWarning[]>([]);
  const [publishMode, setPublishMode] = useState<PublishMode | null>(null);
  /**
   * The CURRENT media session's camera publish decision and tier, resolved ONCE per
   * join/lobby-admit: the decision picks the codec path, the tier sizes the capture,
   * the publish encodings and the effects processor's canvas (PUBLISH_TIER_CAPS) -
   * one session-tier decision keeps every acquisition and the raw<->canvas swaps at
   * the same dimensions. Read by the toggles, re-acquisitions and the machine, which
   * run outside the join flow.
   */
  const publishDecisionRef = useRef<PublishDecision | null>(null);
  const publishTierRef = useRef<PublishTier>('medium');
  const [screenBusy, setScreenBusy] = useState(false);
  const [screenError, setScreenError] = useState<string | null>(null);
  const [controlError, setControlError] = useState<string | null>(null);
  // Layouts, resilience and the Verba shell: the pinned tile key (identity or screen:<identity>,
  // null = automatic), the active speaker identities (loudest first), the explicit connection
  // phase, why the room dropped, the open side panel and the elapsed-meeting counter.
  const [pinnedKey, setPinnedKey] = useState<string | null>(null);
  const [activeSpeakers, setActiveSpeakers] = useState<readonly string[]>([]);
  const [connPhase, setConnPhase] = useState<ConnectionPhase>('disconnected');
  const [dropReason, setDropReason] = useState<DisconnectReasonKind | null>(null);
  const [panel, setPanel] = useState<PanelMode | null>(null);
  // The collaborative board overlay (ADR 0012): meetings toggle it from the control bar.
  const [boardOpen, setBoardOpen] = useState(false);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  // Realtime channel inputs: the validated display name and the join credentials are fixed per
  // join; the hook opens /ws only while both exist and the room is connected.
  const [displayName, setDisplayName] = useState<string | null>(null);
  const [credentials, setCredentials] = useState<JoinCredentials | null>(null);
  /**
   * The deferred media credentials the admit delivered (CR-15): the held join's REST response
   * carried none (mediaDeferred), so this lands on the lobby_admitted frame and flips the page
   * out of the waiting screen into the media connect. Null while the join is still held - the
   * single signal the waiting screen gate reads.
   */
  const [admittedMedia, setAdmittedMedia] = useState<LobbyAdmittedMedia | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  // Hand raise: optimistic flip for instant feedback, reconciled (and cleared) by the server's
  // hand frame echo.
  const [optimisticHand, setOptimisticHand] = useState<boolean | null>(null);
  const [reactionsOpen, setReactionsOpen] = useState(false);
  const [floaters, setFloaters] = useState<readonly ReactionFloater[]>([]);
  // Recording (M6): the start/stop request gate, the panel refresh signal bumped after every
  // completed toggle, and the id of the recording whose banner was dismissed this session.
  const [recBusy, setRecBusy] = useState(false);
  const [recRefreshKey, setRecRefreshKey] = useState(0);
  // Bumped after every moderation dispatch: re-fetches the recent-actions audit (CR-22) without
  // waiting for its soft poll.
  const [recentActionsKey, setRecentActionsKey] = useState(0);
  const [recBannerDismissedId, setRecBannerDismissedId] = useState<number | null>(null);
  const leaveIntentRef = useRef(false);
  /**
   * The background processor handed over from the prejoin (processed camera). Its track is
   * published instead of a fresh capture; disposal belongs to the room's leave paths.
   */
  const backgroundRef = useRef<BackgroundHandover | null>(null);
  /**
   * The in-call camera-effects machine (background treatment, blur strength, color filter):
   * it swaps the processed canvas track into the camera publication via replaceTrack on
   * every store-driven change, without a rejoin. Created lazily - its publication getter
   * must read the CURRENT Room behind roomRef.
   */
  const cameraFxRef = useRef<CameraEffectsMachine | null>(null);
  /** The last camera-effects request seen from the store (the subscription's diff guard). */
  const cameraFxRequestRef = useRef<CameraEffectsRequest | null>(null);
  const [, setTick] = useState(0);
  const roomRef = useRef<Room | null>(null);
  /**
   * The join token the re-mint path presents: the initial join's token at first, then the
   * newest token each successful renewal produced (a remint renews in place; the 401
   * fallback's fresh join mints a new identity and its token becomes the renewable one). A
   * ref, not state: renewals must not flip the realtime client's initialToken prop, which
   * would restart the socket on every reconnect.
   */
  const renewableTokenRef = useRef<string | null>(null);
  /**
   * Remaining-life window of the current renewable token (mintedAt/expiresAt): the input of the
   * proactive renewal that keeps a warm, valid token across the session (the client side of
   * CONTROL_MEDIA_TOKEN_TTL, CR-13). Null while no session lives.
   */
  const tokenWindowRef = useRef<TokenWindow | null>(null);
  /**
   * The auto media-reconnect entry the Disconnected handler calls for an unintended drop
   * (network loss, background-throttled signalling): it re-mints credentials and reconnects
   * the media session with a VALID token instead of landing the user on the dropped screen.
   * `immediate` skips the backoff slot (the 'online' fast-path). A ref because
   * buildRoomHandlers is identity-stable and cannot close over the recovery, and so the
   * function can re-dispatch itself for the next attempt without a self-referencing
   * dependency.
   */
  const mediaDropRecoveryRef = useRef<((reason: unknown, immediate?: boolean) => void) | null>(
    null,
  );
  /** Pending auto media-reconnect timer of the current outage (cleared on teardown/join). */
  const mediaRecoveryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  /** Recovery attempts burnt on the current outage; reset on a successful reconnect. */
  const mediaRecoveryAttemptRef = useRef(0);
  /** When the current outage started holding for connectivity (0 = not holding). */
  const mediaRecoveryHoldSinceRef = useRef(0);
  /** The drop reason of the current outage; replayed by retries and the 'online' fast-path. */
  const mediaRecoveryReasonRef = useRef<unknown>(null);
  const floaterIdRef = useRef(0);
  const floaterTimersRef = useRef<Set<ReturnType<typeof setTimeout>>>(new Set());
  const toastTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  /**
   * True while a local microphone toggle is in flight: the TrackMuted event the SDK fires for
   * it must not read as a moderator mute. Remote mutes arrive with no local toggle pending.
   */
  const localMicIntentRef = useRef(false);
  // Prejoin options carried into the room session: the own-tile mirror flip and the speaker
  // output device (applied to the remote AudioSink elements via setSinkId, best-effort).
  const [mirrorVideo, setMirrorVideo] = useState(true);
  const [speakerSinkId, setSpeakerSinkId] = useState<string | null>(null);
  // Syncra call-screen state: the local connection quality (top-bar signal chip), the invite
  // and shortcuts dialogs, the hotkey feedback pill, the unread chat counter (chat frames
  // received while the chat panel is closed), the fullscreen flag and the end-for-all loop.
  const [localQuality, setLocalQuality] = useState('unknown');
  // Every member's LiveKit connection quality by identity (the unified room header's
  // aggregate chip and the people panels' per-row bars). The local participant is in the map
  // too, keyed by their identity.
  const [qualityMap, setQualityMap] = useState<ReadonlyMap<string, string>>(new Map());
  // Control-plane RTT of the own session (the unified room header's ping chip): the prejoin
  // probe re-run on an interval while connected. Null until the first probe answers.
  const [pingMs, setPingMs] = useState<number | null>(null);
  const [inviteOpen, setInviteOpen] = useState(false);
  // The staff's invitation-links dialog (CR-20); opened from the people tab's staff button.
  const [invitesOpen, setInvitesOpen] = useState(false);
  /**
   * The invitation secret of a `/r/<slug>?invite=...` deep link (CR-20), read once at mount and
   * kept through every guest join POST of the session (the initial join, the reconnect fallbacks
   * and the lobby retry): a live invitation auto-admits past the lobby. Never sent on the SSO
   * path (the contract keeps the body alternatives disjoint). Capped defensively - the server
   * pattern-checks the real shape (43-char base64url) either way.
   */
  const inviteTokenRef = useRef<string | null>(parseInviteToken(window.location.search));
  const [shortcutsOpen, setShortcutsOpen] = useState(false);
  // Effects dialog (opened from the «Ещё» menu): the microphone processing switches and the
  // camera background choice. The switches ride the preference store — the subscription below
  // applies a change to the live publication without a rejoin; `effective` is what the active
  // track actually reports (getSettings), `storedPrefs` feeds the camera section.
  const [effectsOpen, setEffectsOpen] = useState(false);
  const [micEffects, setMicEffects] = useState<AudioEffectSettings>((): AudioEffectSettings =>
    audioEffectSettingsFromPrefs(devicePrefsStore().get()),
  );
  const [micEffectsEffective, setMicEffectsEffective] = useState<AudioEffectSettings | null>(null);
  const [micEffectsBusy, setMicEffectsBusy] = useState(false);
  const [storedPrefs, setStoredPrefs] = useState<DevicePrefs>((): DevicePrefs =>
    devicePrefsStore().get(),
  );
  const micEffectsRef = useRef<AudioEffectSettings>(micEffects);
  /** The join request of the CURRENT session: the mic device id and switches for re-acquires. */
  const joinMediaRef = useRef<PrejoinJoinRequest | null>(null);
  /**
   * The current session's join request without the media handover: the lobby-rejected screen's
   * «Try again» rebuilds a fresh join from it (a dead handed-over camera track must never be
   * reused, so the retry joins with a raw capture).
   */
  const lastJoinRequestRef = useRef<PrejoinJoinRequest | null>(null);
  const [pill, setPill] = useState<{ key: number; text: string; kind: PillKind } | null>(null);
  const pillTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pillSeqRef = useRef(0);
  const [unreadChat, setUnreadChat] = useState(0);
  const lastReadMessageIdRef = useRef(0);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const rootRef = useRef<HTMLElement | null>(null);
  const [endingForAll, setEndingForAll] = useState(false);
  // Webinar mode (ADR 0010): the own question upvotes (local highlight state - the wire only
  // carries the count), the identities with a grant/revoke request in flight, and the mobile
  // side-panel overlay flag.
  const [ownUpvotes, setOwnUpvotes] = useState<ReadonlySet<number>>(new Set());
  const [speakBusyIds, setSpeakBusyIds] = useState<ReadonlySet<string>>(new Set());
  // Board grants (ADR 0013): the identities with a grant/revoke request in flight, and the
  // fan-out pipe between the realtime frames and the board canvas (element payloads never
  // enter React state - the Excalidraw API applies them imperatively).
  const [boardBusyIds, setBoardBusyIds] = useState<ReadonlySet<string>>(new Set());
  /**
   * The board sync pipe (ADR 0013): listeners of the mounted canvases plus the last-known
   * scene. One per room session - it survives surface closes (only the canvas unsubscribes)
   * and dies with the session teardown (reset()), like the realtime client itself.
   */
  const boardSyncHubRef = useRef<BoardSyncHub | null>(null);
  if (boardSyncHubRef.current === null) {
    boardSyncHubRef.current = new BoardSyncHub();
  }
  // Live cursors (CR-18): the same fan-out pipe for the coalesced board_pointer batches.
  const boardPointerListenersRef = useRef(new Set<(batch: BoardPointerBatch) => void>());
  /**
   * The roster slice the cursor labels need (CR-18): re-pointed after the realtime hook every
   * render, so the label resolver (defined before the hook, stable) always reads the current
   * roster without re-creating per render.
   */
  const boardRosterRef = useRef<{
    you: { readonly identity: string } | null;
    participants: ReadonlyMap<string, { readonly displayName: string }>;
    ownIdentity: string;
  }>({ you: null, participants: new Map(), ownIdentity: '' });
  const [webinarPanelOpen, setWebinarPanelOpen] = useState(false);
  /**
   * Media-handover guard for the speak-grant reconnect: while true, the old room's
   * Disconnected event must not flip the page to dropped/left - a fresh room takes over.
   */
  const speakReconnectingRef = useRef(false);
  /** Re-entry guard of the speak-token fetch (the grant effect can fire twice before await). */
  const speakFlowBusyRef = useRef(false);
  /** Previous speak-grant state of the own identity, to detect the revocation edge. */
  const wasSpeakGrantedRef = useRef(false);
  /**
   * True while the CURRENT media session is viewer-gated: seeded from the join token, flipped
   * false by the mid-room promotion remint (the fresh staff token publishes) and true again by
   * a self demotion - the speak-revoke mirror, where the controls re-lock while the old token
   * lives on, exactly like a revoked speaker's. The speak-token handover does NOT touch it: a
   * granted viewer keeps the grant-driven gating (and its re-lock on revoke) after the upgrade.
   */
  const [mediaIsViewer, setMediaIsViewer] = useState(false);
  /** Re-entry guard of the promotion remint (role frames may repeat; no stacked reconnects). */
  const roleRemintBusyRef = useRef(false);
  /** Previous staff-ness of the own role, to detect the mid-room promotion/demotion edge. */
  const wasOwnStaffRef = useRef<boolean | null>(null);
  /**
   * True while a media handover (speak-token or promotion) is minting credentials or swapping
   * the Room: the room behind roomRef is about to be - or has just been - replaced, so its
   * publish permissions are stale (the old viewer room answers publish attempts with exactly
   * "failed to publish track, insufficient permissions"). Toggles and hotkeys read this and
   * stay inert for the handover window instead of racing the swap.
   */
  const mediaHandoverBusyRef = useRef(false);
  /**
   * The realtime action set, re-pointed on every render: board plumbing defined before the
   * hook reads its sendBoardUpdate through this ref (the hook call comes later in the body).
   */
  const realtimeActionsRef = useRef<RealtimeRoomActions | null>(null);

  const rerender = useCallback((): void => {
    setTick((tick) => tick + 1);
  }, []);

  /** Raw device/system detail for an interpolated error frame; never renders empty. */
  const detailOf = useCallback(
    (err: unknown): string => rawErrorMessage(err) ?? t('room.unexpected_error'),
    [t],
  );

  /**
   * Builds (once) the in-call camera-effects machine. The publication handle wraps the
   * CURRENT camera publication's LocalTrack: `replaceTrack` swaps the upstream media
   * stream track inside the same sender - no rejoin, no republish, and the publication's
   * `source: Track.Source.Camera` stamp never changes (the T1 fix stays intact on every
   * path). A rebuild that fails to produce a processed outcome resolves null and the
   * machine keeps the previous track published; onError surfaces the control-bar toast.
   */
  const getCameraFx = useCallback((): CameraEffectsMachine => {
    cameraFxRef.current ??= createCameraEffectsMachine({
      publication: (): CameraPublicationHandle | null => {
        const publication = roomRef.current?.localParticipant.getTrackPublication(
          Track.Source.Camera,
        );
        const localTrack = publication?.track ?? null;
        if (localTrack === null) {
          return null;
        }
        return {
          mediaStreamTrack: localTrack.mediaStreamTrack,
          replaceTrack: async (track): Promise<void> => {
            await localTrack.replaceTrack(track);
          },
        };
      },
      startProcessor: (capture, request) =>
        startBackgroundProcessor(capture, request.mode, {
          quality: request.quality,
          // The SESSION tier's cap: the canvas publishes at exactly the size the raw
          // capture publishes at, so a raw<->canvas swap never changes dimensions.
          publishCap: PUBLISH_TIER_CAPS[publishTierRef.current],
          blurStrength: request.strength,
          filter: request.filter,
          mask: request.mask,
        }).then((outcome) => (outcome.processed ? outcome : null)),
      onError: (err) => {
        setControlError(t('room.effects_apply_failed', { detail: detailOf(err) }));
      },
    });
    return cameraFxRef.current;
  }, [detailOf, t]);

  // Dev/e2e bridge: the machine's live state for page evaluation (production builds strip it).
  useEffect(() => {
    exposeCameraFxForTests(() => cameraFxRef.current?.snapshot() ?? null);
    // The effects composite stage's perf snapshot (`window.__vcFxPerf()`):
    // backend ('webgl2' | 'canvas2d'), composite ms/frame EMA, published size.
    exposeFxPerfForTests();
  }, []);

  // Dev/e2e bridge: the camera publication's LiveKit sender stats (per-layer frame
  // dimensions, fps, target bitrate) for the publish-quality measurements.
  useEffect(() => {
    exposeCamStatsForTests(async () => {
      const room = roomRef.current;
      if (room === null) {
        return null;
      }
      const videoTrack = room.localParticipant.getTrackPublication(Track.Source.Camera)?.videoTrack;
      if (videoTrack === null || videoTrack === undefined) {
        return null;
      }
      const stats = await videoTrack.getSenderStats();
      return stats.map((stat) => ({
        rid: stat.rid,
        frameWidth: stat.frameWidth,
        frameHeight: stat.frameHeight,
        framesPerSecond: stat.framesPerSecond,
        targetBitrate: stat.targetBitrate,
        bytesSent: stat.bytesSent ?? 0,
        timestamp: stat.timestamp,
      }));
    });
  }, []);

  // Dev/e2e bridge: every subscribed remote video track's LiveKit receiver stats (decoded
  // frame dimensions, fps, bytes received) - the RECEIVING half of the quality evidence
  // (publish side above): what the viewer actually gets per tile, per publication source.
  useEffect(() => {
    exposeReceiverStatsForTests(async (): Promise<readonly ReceiverVideoStatSnapshot[]> => {
      const room = roomRef.current;
      if (room === null) {
        return [];
      }
      const snapshots: ReceiverVideoStatSnapshot[] = [];
      for (const participant of room.remoteParticipants.values()) {
        for (const publication of participant.trackPublications.values()) {
          if (publication.kind !== Track.Kind.Video || publication.videoTrack === undefined) {
            continue;
          }
          // The `in` guard narrows the publication union to the remote track (only
          // RemoteVideoTrack carries receiver stats).
          const videoTrack = publication.videoTrack;
          if (!('getReceiverStats' in videoTrack)) {
            continue;
          }
          const stats = await videoTrack.getReceiverStats();
          if (stats === undefined) {
            continue;
          }
          snapshots.push({
            identity: participant.identity,
            source: publication.source === Track.Source.ScreenShare ? 'screen_share' : 'camera',
            frameWidth: stats.frameWidth ?? 0,
            frameHeight: stats.frameHeight ?? 0,
            framesDecoded: stats.framesDecoded,
            bytesReceived: stats.bytesReceived ?? 0,
            timestamp: stats.timestamp,
          });
        }
      }
      return snapshots;
    });
  }, []);

  /**
   * In-call application of a camera-effects change. The media-handover window drops it
   * (the exact toggle pattern: the Room behind roomRef is being replaced, a swap against
   * the old viewer room races the handover); otherwise the preference stays stored and
   * the machine applies the request to the live publication. Failures surface through
   * onError - the control-bar toast; the stored preference never rolls back.
   */
  const applyCameraEffectsInCall = useCallback(
    async (next: CameraEffectsRequest): Promise<void> => {
      if (roomRef.current === null || mediaHandoverBusyRef.current) {
        return;
      }
      // The machine sequences racing applies internally (a stale rebuild loses and
      // releases itself); a slow swap just lands late.
      await getCameraFx().apply(next);
      rerender();
    },
    [getCameraFx, rerender],
  );

  /**
   * Reads the effective switches from the live published microphone track (getSettings) into
   * the dialog state, and exposes them to the dev-only e2e bridge (window.__vcMicSettings).
   */
  const refreshMicEffectsEffective = useCallback((): void => {
    const localTrack =
      roomRef.current?.localParticipant.getTrackPublication(Track.Source.Microphone)?.audioTrack ??
      null;
    const mediaTrack = localTrack === null ? null : localTrack.mediaStreamTrack;
    setMicEffectsEffective(readAudioEffectSettings(mediaTrack));
    exposeMicSettingsForTests(mediaTrack);
  }, []);

  /**
   * In-call switch of the microphone processing; the preference-store subscription calls this
   * whenever an audio-effect key changes. applyConstraints on the live published track first
   * (no gap on Chrome), the SDK's restartTrack re-acquisition as the fallback where the
   * browser rejects the constraints (~1 s gap, no republish). A total failure surfaces the
   * control-bar error toast and keeps the session as-is: the preference stays stored and the
   * dialog row shows what the track actually does. Without a live track (start-muted join,
   * mic off, viewer) there is nothing to apply: the next acquisition captures with the new
   * switches (handleToggleMic / reconnectMedia read the store-backed ref).
   */
  const applyMicEffectsInCall = useCallback(
    async (next: AudioEffectSettings): Promise<void> => {
      const room = roomRef.current;
      if (room === null) {
        return;
      }
      const localTrack = room.localParticipant.getTrackPublication(
        Track.Source.Microphone,
      )?.audioTrack;
      if (localTrack === undefined) {
        refreshMicEffectsEffective();
        return;
      }
      setMicEffectsBusy(true);
      try {
        await applyMicEffectSettings(
          localTrack,
          next,
          microphoneCaptureOptions(joinMediaRef.current?.micDeviceId ?? null, next),
        );
      } catch (err: unknown) {
        setControlError(t('room.effects_apply_failed', { detail: detailOf(err) }));
      } finally {
        setMicEffectsBusy(false);
        refreshMicEffectsEffective();
        rerender();
      }
    },
    [detailOf, refreshMicEffectsEffective, rerender, t],
  );

  // Fresh effective-state read every time the dialog opens (programmatic opens do not run
  // Radix's onOpenChange(true)); close needs no refresh.
  useEffect(() => {
    if (effectsOpen) {
      refreshMicEffectsEffective();
    }
  }, [effectsOpen, refreshMicEffectsEffective]);

  // The reactive preference path (the first in-room effect switch in the product): the
  // effects dialog writes through the store, this subscription applies the change to the
  // active session without a rejoin - audio switches through applyConstraints/restart,
  // the camera effects through the replaceTrack machine. Re-subscribes only when an
  // apply path identity changes.
  useEffect(() => {
    const store = devicePrefsStore();
    setStoredPrefs(store.get());
    const current = audioEffectSettingsFromPrefs(store.get());
    micEffectsRef.current = current;
    setMicEffects(current);
    cameraFxRequestRef.current = effectsRequestFromPrefs(store.get());
    return store.subscribe((prefs) => {
      setStoredPrefs(prefs);
      const next = audioEffectSettingsFromPrefs(prefs);
      const previous = micEffectsRef.current;
      micEffectsRef.current = next;
      setMicEffects(next);
      if (
        next.echoCancellation !== previous.echoCancellation ||
        next.noiseSuppression !== previous.noiseSuppression ||
        next.autoGainControl !== previous.autoGainControl
      ) {
        void applyMicEffectsInCall(next);
      }
      const nextCameraFx = effectsRequestFromPrefs(prefs);
      const previousCameraFx = cameraFxRequestRef.current;
      cameraFxRequestRef.current = nextCameraFx;
      if (requestsDiffer(previousCameraFx, nextCameraFx)) {
        void applyCameraEffectsInCall(nextCameraFx);
      }
    });
  }, [applyMicEffectsInCall, applyCameraEffectsInCall]);

  /**
   * Tears a live or failing session down and returns to the prejoin screen (same room,
   * remembered devices) without a page reload; the next Join starts a fresh session. Used by
   * the manual rejoin affordance and by the SSO session-expiry path.
   */
  const dropToPrejoin = useCallback((notice: string | null): void => {
    leaveIntentRef.current = true;
    // Drop also ends the handed-over background processor; a rejoin builds a fresh one.
    backgroundRef.current?.dispose();
    backgroundRef.current = null;
    void roomRef.current?.disconnect();
    roomRef.current = null;
    setCredentials(null);
    renewableTokenRef.current = null;
    tokenWindowRef.current = null;
    if (mediaRecoveryTimerRef.current !== null) {
      clearTimeout(mediaRecoveryTimerRef.current);
      mediaRecoveryTimerRef.current = null;
    }
    mediaRecoveryAttemptRef.current = 0;
    mediaRecoveryHoldSinceRef.current = 0;
    mediaRecoveryReasonRef.current = null;
    setDropReason(null);
    setActiveSpeakers([]);
    setPinnedKey(null);
    setPanel(null);
    setElapsedSeconds(0);
    setConnPhase('disconnected');
    setOptimisticHand(null);
    setReactionsOpen(false);
    setInviteOpen(false);
    setInvitesOpen(false);
    setShortcutsOpen(false);
    setPill(null);
    setUnreadChat(0);
    lastReadMessageIdRef.current = 0;
    setLocalQuality('unknown');
    setEndingForAll(false);
    setOwnUpvotes(new Set());
    setSpeakBusyIds(new Set());
    setBoardBusyIds(new Set());
    boardSyncHubRef.current?.reset();
    boardPointerListenersRef.current.clear();
    setWebinarPanelOpen(false);
    wasSpeakGrantedRef.current = false;
    speakReconnectingRef.current = false;
    speakFlowBusyRef.current = false;
    setMediaIsViewer(false);
    roleRemintBusyRef.current = false;
    mediaHandoverBusyRef.current = false;
    wasOwnStaffRef.current = null;
    setPhase('pre-join');
    setSsoNotice(notice);
  }, []);

  // Keeps the tab's SSO session alive while the room is open: the scheduler rotates the token
  // pair near expiry, so a reconnect minutes later re-sends a token Keycloak still accepts.
  useEffect(() => {
    const storage = browserSsoStorage();
    if (storage === null) {
      return undefined;
    }
    return startSsoAutoRefresh({
      config: ssoConfigFromEnv(),
      storage,
      onRefreshed: (fresh) => setSsoToken(fresh),
    });
  }, []);

  /**
   * One SSO join POST with session recovery: a token expiring within 60 s is refreshed before
   * the attempt (prejoin path), and an sso_invalid answer triggers one refresh + one retry
   * before the caller surfaces the expired-session notice. No usable storage degrades to a
   * plain join.
   */
  const joinWithSsoRecovery = useCallback(
    (accessToken: string): Promise<JoinCredentials> => {
      const storage = browserSsoStorage();
      return callWithSsoRecovery(accessToken, (token) => joinRoom(slug, { ssoToken: token }), {
        config: ssoConfigFromEnv(),
        storage,
        rejected: (err: unknown): boolean =>
          err instanceof JoinApiError && err.code === 'sso_invalid',
        onRefreshed: (fresh) => setSsoToken(fresh),
      });
    },
    [slug],
  );

  /**
   * Renews the join credentials (token, SFU url, TURN) for the SAME identity - the shared
   * engine of the realtime reconnects and of the mid-room promotion handover. Guests renew the
   * ORIGINAL join token through the remint endpoint (no presence, hand or chat-authorship
   * duplication); SSO joins re-send the ssoToken, whose identity the server re-resolves from
   * the verified token. The newest token lands in renewableTokenRef on every success, so later
   * renewals chain. Remint failures: 401 means the renewable token itself expired, so guests
   * fall back to a fresh join with a new identity (a brief duplicate-presence window is the
   * documented limitation) and the fallback token becomes the renewable one; 429 (per-identity
   * bucket, 5/min) retries once after a short backoff, then propagates - the reconnect-exhausted
   * path takes over; 403 kicked_from_room and everything else propagate too, with the kick
   * mapped to the removed screen by the realtime hook. An sso_invalid answer on the SSO path
   * means the Keycloak token expired between joins: one refresh + one retry is attempted first
   * (joinWithSsoRecovery), and only a still-rejected token drops the sign-in session and falls
   * back to the prejoin with a notice - never an automatic redirect back to Keycloak.
   */
  const mintJoinCredentials = useCallback(async (): Promise<JoinCredentials> => {
    if (ssoToken !== null) {
      try {
        return await joinWithSsoRecovery(ssoToken);
      } catch (err: unknown) {
        if (err instanceof JoinApiError && err.code === 'sso_invalid') {
          const storage = browserSsoStorage();
          if (storage !== null) {
            clearSsoSession(storage);
          }
          setSsoToken(null);
          dropToPrejoin(t('sso.session_expired'));
        }
        throw err;
      }
    }
    const name = displayName;
    if (name === null) {
      throw new Error('cannot re-mint a token without a display name');
    }
    const renewable = renewableTokenRef.current;
    if (renewable === null) {
      // Defensive: no renewable token (a live room always has one) - re-run the guest join,
      // which mints a fresh server identity.
      const fresh = await joinRoom(slug, guestJoinBody(name, inviteTokenRef.current));
      renewableTokenRef.current = fresh.token;
      return fresh;
    }
    const renew = (): Promise<JoinCredentials> => remintTokenApi(slug, renewable);
    let renewed: JoinCredentials;
    try {
      renewed = await renew();
    } catch (err: unknown) {
      if (!(err instanceof JoinApiError)) {
        throw err;
      }
      if (err.status === 401) {
        // The renewable token expired: the server cannot renew it, so a fresh guest join is
        // the only way back. The new identity duplicates the old presence until the server
        // prunes the stale row; the reconnect state frame re-seeds the own presence.
        const fallback = await joinRoom(slug, guestJoinBody(name, inviteTokenRef.current));
        renewableTokenRef.current = fallback.token;
        return fallback;
      }
      if (err.status !== 429) {
        // kicked_from_room and every other failure propagate.
        throw err;
      }
      // Per-identity remint bucket drained: back off once and retry. A second refusal (or any
      // other retry failure) propagates to the reconnect-exhausted path.
      await delay(REMINT_RETRY_DELAY_MS);
      renewed = await renew();
    }
    renewableTokenRef.current = renewed.token;
    return renewed;
  }, [slug, displayName, ssoToken, dropToPrejoin, joinWithSsoRecovery]);

  /**
   * Renews the join credentials and remembers the fresh token's validity window: the newest
   * token becomes the renewable one AND the proactive-renewal baseline (CR-13), so the next
   * silent mint lands at ~60% of THIS token's TTL. Every renewal path funnels through here,
   * which keeps the window in step with renewableTokenRef by construction.
   */
  const renewJoinCredentials = useCallback(async (): Promise<JoinCredentials> => {
    const fresh = await mintJoinCredentials();
    tokenWindowRef.current = credentialsWindow(fresh);
    return fresh;
  }, [mintJoinCredentials]);

  /** The realtime reconnects only need the token; the promotion handover needs the full shape. */
  const remintToken = useCallback(
    async (): Promise<string> => (await renewJoinCredentials()).token,
    [renewJoinCredentials],
  );

  // Proactive join-token renewal (the client side of CONTROL_MEDIA_TOKEN_TTL, CR-13). While a
  // session is connected, re-mint silently once the current token has burnt ~60% of its TTL and
  // keep the fresh token warm for the NEXT reconnect - livekit-client 2.22.3 exposes no
  // token-update path into a live Room (room.connect is the only documented entry; the signal
  // onTokenRefresh is server-pushed), so swapping the credentials mid-call would mean dropping
  // a healthy call, which is exactly what this must not do. What the warm token buys: every
  // reconnect path (the realtime hook, the media drop recovery below) mints or consumes fresh
  // credentials, and the 4409 expiry close of the control WS lands far out. The check also
  // re-runs when the tab becomes visible again - background throttling delays timers, and this
  // is where a hidden tab catches up instead of reconnecting on a stale token. A failed renewal
  // is best effort: the next tick (and every reconnect) retries.
  useEffect(() => {
    if (phase !== 'connected') {
      return undefined;
    }
    let busy = false;
    const check = (): void => {
      const windowEntry = tokenWindowRef.current;
      if (busy || windowEntry === null) {
        return;
      }
      const ttl = windowEntry.expiresAt - windowEntry.mintedAt;
      const remaining = windowEntry.expiresAt - Date.now();
      if (!(ttl > 0) || remaining > ttl * 0.4) {
        return;
      }
      busy = true;
      renewJoinCredentials()
        .catch(() => {
          // Best effort; the tick retries and the reconnect paths mint on demand.
        })
        .finally(() => {
          busy = false;
        });
    };
    check();
    const timer = setInterval(check, MEDIA_TOKEN_RENEW_CHECK_MS);
    const onVisibility = (): void => {
      if (document.visibilityState === 'visible') {
        check();
      }
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [phase, renewJoinCredentials]);

  /** Shows a transient toast; the newest one wins, all clear themselves. */
  const showToast = useCallback((message: string): void => {
    setToast(message);
    if (toastTimerRef.current !== null) {
      clearTimeout(toastTimerRef.current);
    }
    toastTimerRef.current = setTimeout(() => {
      toastTimerRef.current = null;
      setToast(null);
    }, TOAST_CLEAR_MS);
  }, []);

  /**
   * Shows the hotkey feedback pill above the stage (Syncra): auto-hides after 1.1s, the
   * timer resets when a new pill fires while one is visible.
   */
  const showPill = useCallback((text: string, kind: PillKind): void => {
    if (pillTimerRef.current !== null) {
      clearTimeout(pillTimerRef.current);
    }
    pillSeqRef.current += 1;
    setPill({ key: pillSeqRef.current, text, kind });
    pillTimerRef.current = setTimeout(() => {
      pillTimerRef.current = null;
      setPill(null);
    }, PILL_VISIBLE_MS);
  }, []);

  /**
   * Cooperative moderation receipt: a moderator asked this participant to stop sharing, so the
   * screen share stops immediately (best effort) and a toast explains why.
   */
  const handleModeration = useCallback(
    (action: string): void => {
      if (action !== 'stop_share') {
        return;
      }
      const room = roomRef.current;
      if (room !== null && isScreenSharing(room.localParticipant)) {
        room.localParticipant.setScreenShareEnabled(false).catch(() => {
          // The share is being torn down server-side regardless; nothing to surface.
        });
      }
      showToast(t('room.toast_stop_share'));
    },
    [showToast],
  );

  /** Every incoming reaction (own included) becomes a floating emoji over the stage. */
  const handleReaction = useCallback(
    (reaction: { readonly identity: string; readonly emoji: string }): void => {
      floaterIdRef.current += 1;
      const id = floaterIdRef.current;
      const leftPct = 8 + ((id * 29) % 74);
      setFloaters((current) => [
        ...current.slice(-(MAX_FLOATERS - 1)),
        { id, emoji: reaction.emoji, leftPct },
      ]);
      const timer = setTimeout(() => {
        floaterTimersRef.current.delete(timer);
        setFloaters((current) => current.filter((floater) => floater.id !== id));
      }, REACTION_FLOAT_MS);
      floaterTimersRef.current.add(timer);
    },
    [],
  );

  /** The server rejected a moderation or role request with an error frame. */
  const handleModerationError = useCallback(
    (code: string): void => {
      showToast(t(`errors.moderation_${moderationErrorKind(code)}`));
    },
    [showToast, t],
  );

  /**
   * Hook adapters: the wire frames feed the board sync pipe. The pipe keeps the last-known
   * scene current from BOTH directions - remote deltas and snapshots land whether or not a
   * canvas is mounted, and the outbound diff is recorded too (the hub never echoes the
   * sender) - so a surface closed and reopened in the same live room re-mounts with the full
   * scene instead of an empty canvas.
   */
  const handleBoardSyncUpdate = useCallback(
    (update: { readonly identity: string; readonly elements: readonly unknown[] }): void => {
      boardSyncHubRef.current?.applyUpdate(update.elements);
    },
    [],
  );
  const handleBoardSyncState = useCallback(
    (snapshot: { readonly elements: readonly unknown[] }): void => {
      boardSyncHubRef.current?.applyState(snapshot.elements);
    },
    [],
  );

  /** Subscribes a mounting board canvas to the sync pipe; the known scene replays to it
   * immediately (a late subscriber gets the handshake-beaten snapshot, a reopened surface the
   * scene merged since), then it lives on the live fan-out. */
  const subscribeBoardSync = useCallback((listener: (sync: BoardSync) => void): (() => void) => {
    const hub = boardSyncHubRef.current;
    if (hub === null) {
      return () => undefined;
    }
    return hub.subscribe(listener);
  }, []);

  /**
   * Sends one editor delta through the realtime channel, recording it in the pipe first: the
   * hub fans board_update out to every member EXCEPT the sender, so without this the sender's
   * own strokes would be absent from the scene a reopened surface rebuilds from. The action
   * lives in the hook below, so it is read through a ref that every render re-points - the
   * callback stays stable for the board canvas.
   */
  const sendBoardUpdate = useCallback((elements: readonly BoardSyncElement[]): void => {
    boardSyncHubRef.current?.recordOutbound(elements);
    realtimeActionsRef.current?.sendBoardUpdate(elements);
  }, []);

  /** Fans one coalesced remote-cursor batch out to the mounted board canvases (CR-18). */
  const handleBoardPointerBatch = useCallback((batch: BoardPointerBatch): void => {
    for (const listener of [...boardPointerListenersRef.current]) {
      listener(batch);
    }
  }, []);

  /** Subscribes a mounted board canvas to the remote-cursor batches (CR-18). */
  const subscribeBoardPointers = useCallback(
    (listener: (batch: BoardPointerBatch) => void): (() => void) => {
      boardPointerListenersRef.current.add(listener);
      return () => {
        boardPointerListenersRef.current.delete(listener);
      };
    },
    [],
  );

  /** Sends the local cursor position through the realtime channel (CR-18; throttle upstream). */
  const sendBoardPointer = useCallback((x: number, y: number): void => {
    realtimeActionsRef.current?.sendBoardPointer(x, y);
  }, []);

  /** Display name of any roster identity for a cursor label; unknown identities stay bare. */
  const boardPointerLabelFor = useCallback((identity: string): string => {
    const roster = boardRosterRef.current;
    if (roster.you !== null && roster.you.identity === identity) {
      return roster.ownIdentity;
    }
    return roster.participants.get(identity)?.displayName ?? identity;
  }, []);

  /**
   * Own board-grant edges become toasts, straight off the frame fan-out (not off the derived
   * gate: a per-join ref reset would swallow a grant that lands right after the join). Grants
   * only ride this room's live socket, so a matching identity is always the local user.
   */
  const ownIdentityRef = useRef<string | null>(null);
  const handleOwnBoardGrant = useCallback(
    (grant: { readonly identity: string; readonly granted: boolean }): void => {
      if (grant.identity !== ownIdentityRef.current) {
        return;
      }
      showToast(t(grant.granted ? 'board.granted_toast' : 'board.revoked_toast'));
    },
    [showToast, t],
  );

  /** Host action: grant one participant the right to draw (POST; the frame moves the roster). */
  const handleGrantBoard = useCallback(
    async (targetIdentity: string): Promise<void> => {
      const token = renewableTokenRef.current;
      if (token === null || boardBusyIds.has(targetIdentity)) {
        return;
      }
      setBoardBusyIds((current) => new Set(current).add(targetIdentity));
      try {
        await grantBoard(slug, targetIdentity, token);
      } catch {
        showToast(t('board.action_failed'));
      } finally {
        setBoardBusyIds((current) => {
          const next = new Set(current);
          next.delete(targetIdentity);
          return next;
        });
      }
    },
    [slug, boardBusyIds, showToast, t],
  );

  /** Host action: revoke one granted board editor (DELETE; a revoked client turns read-only). */
  const handleRevokeBoard = useCallback(
    async (targetIdentity: string): Promise<void> => {
      const token = renewableTokenRef.current;
      if (token === null || boardBusyIds.has(targetIdentity)) {
        return;
      }
      setBoardBusyIds((current) => new Set(current).add(targetIdentity));
      try {
        await revokeBoard(slug, targetIdentity, token);
      } catch {
        showToast(t('board.action_failed'));
      } finally {
        setBoardBusyIds((current) => {
          const next = new Set(current);
          next.delete(targetIdentity);
          return next;
        });
      }
    },
    [slug, boardBusyIds, showToast, t],
  );

  /** Shows the moderator-mute receipt; the mic button flips through the re-render. */
  const showRemotelyMuted = useCallback((): void => {
    showToast(t('room.toast_remotely_muted'));
  }, [showToast]);

  /**
   * Reconciles the room recording state after the state snapshot lands. The server answers
   * the WS auth with the roster only - no recording state - so mid-recording sessions learn
   * about an active recording from the recordings list. The list is moderator/admin gated
   * (the hook passes the just-confirmed role); participants skip the fetch entirely (the
   * frames are their source).
   */
  const reconcileRecording = useCallback(
    async (token: string, role: ParticipantRole): Promise<RecordingSnapshot | null> => {
      if (!canModerate(role)) {
        return null;
      }
      const list = await listRecordings(slug, token);
      const active = list.find((recording) => isActiveRecordingStatus(recording.status));
      return active === undefined ? null : { state: active.status, recordingId: active.id };
    },
    [slug],
  );

  /** Shows a localized toast for a failed recording start/stop request. */
  const showRecordingError = useCallback(
    (err: unknown): void => {
      showToast(t(`recording.error_${recordingErrorKind(err)}`));
    },
    [showToast, t],
  );

  /** Starts the room recording; the WS frame flips the indicator when the server accepts. */
  const handleRecordingStart = useCallback(async (): Promise<void> => {
    const token = renewableTokenRef.current;
    if (token === null || recBusy) {
      return;
    }
    setRecBusy(true);
    try {
      await startRecording(slug, token);
    } catch (err: unknown) {
      showRecordingError(err);
    } finally {
      setRecBusy(false);
      setRecRefreshKey((key) => key + 1);
    }
  }, [slug, recBusy, showRecordingError]);

  /** Stops the room recording (the confirm gate has already asked). */
  const handleRecordingStop = useCallback(async (): Promise<void> => {
    const token = renewableTokenRef.current;
    if (token === null || recBusy) {
      return;
    }
    setRecBusy(true);
    try {
      await stopRecording(slug, token);
    } catch (err: unknown) {
      showRecordingError(err);
    } finally {
      setRecBusy(false);
      setRecRefreshKey((key) => key + 1);
    }
  }, [slug, recBusy, showRecordingError]);

  /**
   * Stable identity for the realtime hook's onLobbyAdmitted (CR-15): the implementation is the
   * hoisted handleLobbyAdmittedImpl declared further down the component.
   */
  const handleLobbyAdmitted = useCallback((media: LobbyAdmittedMedia): void => {
    void handleLobbyAdmittedImpl(media);
  }, []);

  const { state: realtime, actions: realtimeActions } = useRealtimeRoom({
    slug,
    enabled: phase === 'connected' && credentials !== null,
    initialToken: credentials?.token ?? null,
    // The join response seeds the provisional own presence until the server's state frame
    // lands and remains authoritative.
    initialIdentity: credentials?.identity ?? null,
    initialRole: joinedRole,
    initialKind: credentials?.kind ?? null,
    remintToken,
    onModeration: handleModeration,
    onModerationError: handleModerationError,
    onBoardGrant: handleOwnBoardGrant,
    onBoardUpdate: handleBoardSyncUpdate,
    onBoardState: handleBoardSyncState,
    onBoardPointer: handleBoardPointerBatch,
    onReaction: handleReaction,
    // CR-15: the admit's media credentials connect the SFU from the waiting screen. The
    // handler is a hoisted function declaration, so the stable callback can sit before its
    // textual declaration.
    onLobbyAdmitted: handleLobbyAdmitted,
    reconcileRecording,
  });
  // Re-point the action ref every render (see realtimeActionsRef): the board plumbing defined
  // before the hook reads the latest sendBoardUpdate through it.
  realtimeActionsRef.current = realtimeActions;

  const ownRole = realtime.you?.role ?? joinedRole;
  // The server-assigned own identity (the state frame wins over the join response seed).
  const ownIdentity = realtime.you?.identity ?? identity;
  // Re-point the identity ref every render: the board-grant callback defined before the hook
  // reads the latest own identity through it.
  ownIdentityRef.current = ownIdentity;
  // The cursor-label roster (CR-18), same re-point pattern.
  boardRosterRef.current = {
    you: realtime.you,
    participants: realtime.participants,
    ownIdentity: ownIdentity ?? '',
  };

  // The own role gates the recordings tab: a mid-session demotion closes it immediately.
  useEffect(() => {
    if (panel === 'recordings' && !canModerate(ownRole)) {
      setPanel(null);
    }
  }, [panel, ownRole]);

  // ---- Room kind (CR-12) ----
  // The kind is authoritative from the wire: the join response seeds it, the WS state frame
  // corrects it. The JWT grant heuristic below is only the fallback for an older server whose
  // state frame arrives without a kind.
  // The ACTIVE media token's viewer shape drives the speak-grant flow (grant/revoke edges) and
  // the room-kind heuristic: the held join's deferred token (CR-15) carries no grants at all,
  // so the admitted credentials replace it as the basis the moment they arrive.
  const activeMediaToken = admittedMedia?.token ?? credentials?.token ?? null;
  const joinIsViewer = activeMediaToken !== null && isViewerJoinToken(activeMediaToken);
  const kindFromWire = realtime.roomKind ?? credentials?.kind ?? null;
  const heuristicWebinar =
    joinIsViewer || realtime.questions.length > 0 || realtime.speakGrants.size > 0;
  const webinarMode =
    phase === 'connected' &&
    (kindFromWire !== null ? kindFromWire === 'webinar' : heuristicWebinar);
  // The board broadcast surface (the board surface amendment): the staff opened the board as
  // the webinar's shared stage. Server-authoritative through the board_surface frames - the
  // toggle sends the switch, the hub's echo (or a late joiner's handshake replay) opens it.
  const boardBroadcastOpen = webinarMode && realtime.boardSurfaceOpen;
  const isHost = canModerate(ownRole);
  const amSpeakGranted = ownIdentity !== null && (realtime.speakGrants.get(ownIdentity) ?? false);
  // Board editor gate (ADR 0013): admins/moderators by role, everyone else by a board grant.
  // The server enforces the same rule on every board_update.
  const amBoardEditor =
    isHost || (ownIdentity !== null && (realtime.boardGrants.get(ownIdentity) ?? false));
  // Publish permission: the current media session's token decides (the SFU rejects anything
  // else, so the UI mirrors the live grant, not the join-time one). A viewer-shaped session
  // publishes only with a live speak grant; the speak-token handover and the mid-room
  // promotion remint both put a publishing token behind mediaIsViewer=false.
  const canPublishNow = mediaIsViewer ? amSpeakGranted : true;
  // Mirror for closures that must read the CURRENT value without re-subscribing (the hotkey
  // effect keeps stable deps; the toggle handlers read the room through refs the same way).
  const canPublishNowRef = useRef(canPublishNow);
  canPublishNowRef.current = canPublishNow;
  // Render-time mirror of the same gate for the control-bar locks: while a media handover is
  // in flight the Room behind roomRef is stale or mid-connect, so the bar locks exactly like
  // a viewer-gated session (the imperative toggle guards stay exact regardless of renders).
  const publishLockedNow = mediaHandoverBusyRef.current || !canPublishNow;
  const grantedIds: ReadonlySet<string> = useMemo(() => {
    const ids = new Set<string>();
    for (const [grantedIdentity, granted] of realtime.speakGrants) {
      if (granted) {
        ids.add(grantedIdentity);
      }
    }
    return ids;
  }, [realtime.speakGrants]);
  const boardGrantedIds: ReadonlySet<string> = useMemo(() => {
    const ids = new Set<string>();
    for (const [grantedIdentity, granted] of realtime.boardGrants) {
      if (granted) {
        ids.add(grantedIdentity);
      }
    }
    return ids;
  }, [realtime.boardGrants]);
  const viewers = viewersCount(realtime.participants);
  const presenceRoles = useMemo(() => {
    const roles = new Map<string, ParticipantRole>();
    for (const [grantedIdentity, participant] of realtime.participants) {
      roles.set(grantedIdentity, participant.role);
    }
    return roles;
  }, [realtime.participants]);

  /** Dispatches one Q&A ask; the contract validation lives in the realtime action. */
  const handleAsk = useCallback(
    (text: string): boolean => realtimeActions.sendQA('ask', { text }),
    [realtimeActions],
  );

  /** Records the own upvote locally (button state) and sends the idempotent server upvote. */
  const handleUpvote = useCallback(
    (questionId: number): void => {
      setOwnUpvotes((current) => new Set(current).add(questionId));
      realtimeActions.sendQA('upvote', { questionId });
    },
    [realtimeActions],
  );

  /** Dispatches one moderator answer (the text is optional per the wire contract). */
  const handleAnswer = useCallback(
    (questionId: number, text: string): void => {
      void (text.length > 0
        ? realtimeActions.sendQA('answer', { questionId, text })
        : realtimeActions.sendQA('answer', { questionId }));
    },
    [realtimeActions],
  );

  /** Dispatches one moderator dismiss; the boards converge on the qa frame echo. */
  const handleDismiss = useCallback(
    (questionId: number): void => {
      realtimeActions.sendQA('dismiss', { questionId });
    },
    [realtimeActions],
  );

  /** Host action: grant one viewer the right to speak (POST; the frame echo moves the roster). */
  const handleGrantSpeak = useCallback(
    async (targetIdentity: string): Promise<void> => {
      const token = renewableTokenRef.current;
      if (token === null || speakBusyIds.has(targetIdentity)) {
        return;
      }
      setSpeakBusyIds((current) => new Set(current).add(targetIdentity));
      try {
        await grantSpeak(slug, targetIdentity, token);
      } catch {
        showToast(t('webinar.speak_action_failed'));
      } finally {
        setSpeakBusyIds((current) => {
          const next = new Set(current);
          next.delete(targetIdentity);
          return next;
        });
      }
    },
    [slug, speakBusyIds, showToast, t],
  );

  /** Host action: revoke one granted speaker (DELETE; a revoked viewer mutes on the frame). */
  const handleRevokeSpeak = useCallback(
    async (targetIdentity: string): Promise<void> => {
      const token = renewableTokenRef.current;
      if (token === null || speakBusyIds.has(targetIdentity)) {
        return;
      }
      setSpeakBusyIds((current) => new Set(current).add(targetIdentity));
      try {
        await revokeSpeak(slug, targetIdentity, token);
      } catch {
        showToast(t('webinar.speak_action_failed'));
      } finally {
        setSpeakBusyIds((current) => {
          const next = new Set(current);
          next.delete(targetIdentity);
          return next;
        });
      }
    },
    [slug, speakBusyIds, showToast, t],
  );

  /** Dispatches one moderation frame from the participants panel. */
  const handleModerate = useCallback(
    (action: ModerationAction, target: string): void => {
      realtimeActions.sendModerate(action, target);
      // The audit row lands server-side a moment after the frame: bump the recent-actions
      // refresh key (CR-22) so the open panel converges without waiting for its poll.
      setRecentActionsKey((key) => key + 1);
    },
    [realtimeActions],
  );

  /** Dispatches one role assignment from the participants panel (admin only). */
  const handleSetRole = useCallback(
    (target: string, role: AssignableRole): void => {
      realtimeActions.sendRole(target, role);
    },
    [realtimeActions],
  );

  /**
   * Builds the room-event handler set. Shared by the join connect and the speak-grant media
   * handover, so a reconnecting broadcaster keeps every receipt (mute, quality, speakers).
   * disposeBackground is a hoisted component function (ref-only body) - not a dependency.
   */
  const buildRoomHandlers = useCallback((): RoomEventHandlers => {
    return {
      rerender,
      onActiveSpeakers: (speakers) => {
        setActiveSpeakers(speakers.map((speaker) => speaker.identity));
      },
      onConnectionState: (state) => {
        if (speakReconnectingRef.current) {
          // Media handover in flight: the old room's state changes must not touch the chip.
          return;
        }
        setConnPhase(connectionPhaseFromRoomState(state));
        rerender();
      },
      onReconnecting: () => {
        setConnPhase('reconnecting');
        rerender();
      },
      onReconnected: () => {
        setConnPhase('connected');
        rerender();
      },
      onConnectionQuality: (quality, participant) => {
        if (participant.isLocal) {
          setLocalQuality(quality);
        }
        // Every member's quality lands in the map the header aggregates and the people
        // panels render per row (the unified room header).
        setQualityMap((current) => {
          const next = new Map(current);
          next.set(participant.identity, quality);
          return next;
        });
      },
      onRemotelyMuted: showRemotelyMuted,
      isLocalMicActionPending: (): boolean => localMicIntentRef.current,
      onDisconnected: (reason) => {
        if (speakReconnectingRef.current) {
          // The speak-grant handover disconnects the old room on purpose; a fresh room has
          // already taken the ref. No dispose, no phase flip, no dropped screen.
          return;
        }
        if (!leaveIntentRef.current && !isUserInitiatedDisconnect(reason)) {
          // An unintended drop (network loss, background-throttled signalling, SFU blip):
          // hand the recovery the drop instead of landing the user on the dropped screen. The
          // recovery re-mints credentials - livekit-client reconnects a dropped Room with the
          // ORIGINAL token, which an expired one turns into a kick out of the call - and only
          // an exhausted recovery falls through to the dropped screen below.
          const recover = mediaDropRecoveryRef.current;
          if (recover !== null) {
            recover(reason);
            return;
          }
        }
        // Leaving/dropping also ends the handed-over background processor (and its input
        // camera capture); a rejoin opens a fresh preview and builds a new one.
        disposeBackground();
        setConnPhase('disconnected');
        setElapsedSeconds(0);
        if (leaveIntentRef.current || isUserInitiatedDisconnect(reason)) {
          setPhase((current) =>
            current === 'connected' || current === 'joining' ? 'left' : current,
          );
        } else {
          setDropReason(disconnectReasonKind(reason));
          setPhase((current) =>
            current === 'connected' || current === 'joining' ? 'dropped' : current,
          );
        }
        rerender();
      },
    };
  }, [rerender, showRemotelyMuted, detailOf]);

  /**
   * Hands the media session over to a fresh Room connected with the given credentials, then
   * restores the requested publish state: the speak-grant handover publishes the microphone
   * right away (the camera follows on its toggle); the promotion handover re-publishes exactly
   * what the user kept enabled across the swap. The handover guard keeps the disconnect of the
   * previous room from reading as a drop.
   */
  const reconnectMedia = useCallback(
    async (
      fresh: JoinCredentials,
      publish: { readonly microphone: boolean; readonly camera: boolean },
    ): Promise<void> => {
      disposeBackground();
      mediaHandoverBusyRef.current = true;
      const previous = roomRef.current;
      if (previous !== null) {
        speakReconnectingRef.current = true;
        try {
          await previous.disconnect();
        } catch {
          // The handover continues regardless: the old session is being replaced.
        }
      }
      const room = new Room(ROOM_OPTIONS);
      registerRoomEvents(room, buildRoomHandlers());
      roomRef.current = room;
      try {
        await room.connect(
          fresh.url,
          fresh.token,
          turnConnectOptions(fresh.turn ?? credentials?.turn),
        );
        if (publish.microphone) {
          // Same capture options as the join publish path: the remembered device and the
          // CURRENT effect switches (the viewer may have toggled them before the handover).
          await room.localParticipant.setMicrophoneEnabled(
            true,
            microphoneCaptureOptions(
              joinMediaRef.current?.micDeviceId ?? null,
              micEffectsRef.current,
            ),
            microphonePublishOptions(),
          );
        }
        if (publish.camera) {
          // Session capture + publish options (tier resolution, decided codec): the fresh
          // capture must come back at the session dimensions, not the SDK defaults.
          const decision = publishDecisionRef.current;
          const tier = publishTierRef.current;
          if (decision !== null) {
            await room.localParticipant.setCameraEnabled(
              true,
              cameraCaptureOptions(joinMediaRef.current?.cameraDeviceId ?? null, tier),
              cameraPublishOptions(decision, tier),
            );
          } else {
            await room.localParticipant.setCameraEnabled(true);
          }
          // The fresh Room published a plain capture: re-apply the stored camera effects
          // to it through the machine (disposeBackground above reset its state; with no
          // effects stored the apply is a no-op).
          await getCameraFx().apply(effectsRequestFromPrefs(devicePrefsStore().get()));
        }
      } finally {
        speakReconnectingRef.current = false;
        // The swap is done (or failed): toggles may operate on the new Room again.
        mediaHandoverBusyRef.current = false;
        rerender();
      }
    },
    [buildRoomHandlers, credentials, rerender],
  );

  /**
   * The terminal fallback of the media drop recovery: exhaustion (or a final failed attempt)
   * lands on the dropped screen exactly as the pre-recovery handler did.
   */
  const finishMediaDropAsDropped = useCallback(
    (reason: unknown): void => {
      disposeBackground();
      setConnPhase('disconnected');
      setElapsedSeconds(0);
      setDropReason(disconnectReasonKind(reason));
      setPhase((current) =>
        current === 'connected' || current === 'joining' ? 'dropped' : current,
      );
      mediaRecoveryAttemptRef.current = 0;
      mediaRecoveryHoldSinceRef.current = 0;
      mediaRecoveryReasonRef.current = null;
      rerender();
    },
    [rerender],
  );

  /**
   * One media-recovery attempt: re-mint the join credentials (a VALID token - the SDK would
   * otherwise retry the drop with the original, possibly expired one) and swap the media
   * session over, restoring whatever the user was publishing. A successful swap resets the
   * counters and toasts the restore; a failure re-dispatches the recovery for the next
   * attempt; exhaustion lands on the dropped screen.
   */
  const attemptMediaRecovery = useCallback(
    (reason: unknown): void => {
      void (async () => {
        // The publish intent of the session the drop killed, read before the swap tears it
        // down (reconnectMedia disconnects the previous Room).
        const dead = roomRef.current;
        const publish = {
          microphone: dead?.localParticipant.isMicrophoneEnabled ?? false,
          camera: dead?.localParticipant.isCameraEnabled ?? false,
        };
        try {
          const fresh = await renewJoinCredentials();
          if (leaveIntentRef.current) {
            return;
          }
          await reconnectMedia(fresh, publish);
          mediaRecoveryAttemptRef.current = 0;
          mediaRecoveryHoldSinceRef.current = 0;
          mediaRecoveryReasonRef.current = null;
          setConnPhase('connected');
          showToast(t('room.toast_connection_restored'));
        } catch {
          // Retry after the backoff; exhaustion lands on the dropped screen.
          if (!leaveIntentRef.current) {
            mediaDropRecoveryRef.current?.(reason);
            return;
          }
        }
        rerender();
      })();
    },
    [reconnectMedia, renewJoinCredentials, showToast, t, rerender],
  );

  /**
   * Auto media-reconnect after an unintended drop: up to MEDIA_DROP_RECOVERY_DELAYS_MS.length
   * attempts with the listed escalating backoffs, each re-minting credentials (see
   * attemptMediaRecovery). `immediate` (the 'online' fast-path) skips the pending backoff
   * slot: connectivity just returned, so the attempt runs at once. Wired through
   * mediaDropRecoveryRef, so the identity-stable buildRoomHandlers can reach it and the
   * function can re-dispatch itself for the next attempt without a self-referencing
   * dependency.
   */
  const recoverFromMediaDrop = useCallback(
    (reason: unknown, immediate = false): void => {
      if (leaveIntentRef.current || mediaRecoveryTimerRef.current !== null) {
        return;
      }
      mediaRecoveryReasonRef.current = reason;
      // Offline hold: with no network an attempt would fail instantly and burn the budget
      // before connectivity even returned (a backgrounded tab can sit out the whole outage).
      // Hold instead, polling navigator.onLine; past the hold cap proceed and fail fast.
      if (!navigator.onLine) {
        const now = Date.now();
        if (mediaRecoveryHoldSinceRef.current === 0) {
          mediaRecoveryHoldSinceRef.current = now;
        }
        if (now - mediaRecoveryHoldSinceRef.current < MEDIA_RECOVERY_ONLINE_HOLD_MS) {
          mediaRecoveryTimerRef.current = setTimeout(() => {
            mediaRecoveryTimerRef.current = null;
            if (!leaveIntentRef.current) {
              mediaDropRecoveryRef.current?.(reason);
            }
          }, MEDIA_RECOVERY_ONLINE_POLL_MS);
          return;
        }
      }
      mediaRecoveryHoldSinceRef.current = 0;
      const attempt = mediaRecoveryAttemptRef.current;
      if (attempt >= MEDIA_DROP_RECOVERY_DELAYS_MS.length) {
        finishMediaDropAsDropped(reason);
        return;
      }
      mediaRecoveryAttemptRef.current = attempt + 1;
      setConnPhase('reconnecting');
      if (immediate) {
        attemptMediaRecovery(reason);
        return;
      }
      mediaRecoveryTimerRef.current = setTimeout(() => {
        mediaRecoveryTimerRef.current = null;
        if (leaveIntentRef.current) {
          return;
        }
        attemptMediaRecovery(reason);
      }, MEDIA_DROP_RECOVERY_DELAYS_MS[attempt] ?? 120_000);
    },
    [attemptMediaRecovery, finishMediaDropAsDropped],
  );

  // Publish the recovery entry for the Disconnected handler, and keep the pending recovery
  // timer from outliving the component.
  useEffect(() => {
    mediaDropRecoveryRef.current = recoverFromMediaDrop;
    return () => {
      mediaDropRecoveryRef.current = null;
    };
  }, [recoverFromMediaDrop]);
  useEffect(
    () => () => {
      if (mediaRecoveryTimerRef.current !== null) {
        clearTimeout(mediaRecoveryTimerRef.current);
        mediaRecoveryTimerRef.current = null;
      }
    },
    [],
  );
  // Connectivity back mid-outage: skip the remaining backoff slot and retry at once (Wi-Fi and
  // VPN recovery must not wait out a 60s+ timer). No pending recovery timer (attempt in
  // flight, recovered, exhausted, or no outage) means nothing to fast-path; the mounted-once
  // listener goes inert on teardown/join because those clear the timer and reset the state.
  useEffect(() => {
    const onOnline = (): void => {
      const timer = mediaRecoveryTimerRef.current;
      if (timer === null || leaveIntentRef.current) {
        return;
      }
      clearTimeout(timer);
      mediaRecoveryTimerRef.current = null;
      mediaDropRecoveryRef.current?.(mediaRecoveryReasonRef.current, true);
    };
    window.addEventListener('online', onOnline);
    return () => {
      window.removeEventListener('online', onOnline);
    };
  }, []);

  // The speak-grant flow for the own identity, viewer joins only: the grant edge mints a
  // publishing token (POST speak-token with the current join JWT) and reconnects the
  // broadcaster; the revocation edge mutes the microphone AND turns the camera off (the
  // SFU-enforced stop stays the moderator mute). Staff never enter the flow: they publish
  // from join (their token carries the grants), so a legacy own speak_grant frame is inert -
  // and so is one aimed at a mid-room promoted moderator (mediaIsViewer already false).
  useEffect(() => {
    if (phase !== 'connected') {
      return;
    }
    const wasGranted = wasSpeakGrantedRef.current;
    wasSpeakGrantedRef.current = amSpeakGranted;
    if (amSpeakGranted === wasGranted) {
      return;
    }
    if (!joinIsViewer || !mediaIsViewer) {
      return;
    }
    if (!amSpeakGranted) {
      const room = roomRef.current;
      if (room !== null) {
        // The revoke mutes the microphone and turns the camera off locally; the host stops
        // the live tracks their side too (the moderator mute).
        if (room.localParticipant.isMicrophoneEnabled) {
          localMicIntentRef.current = true;
          void room.localParticipant
            .setMicrophoneEnabled(false)
            .catch(() => {
              // Best effort; the host's mute action stops the track on the SFU regardless.
            })
            .finally(() => {
              localMicIntentRef.current = false;
              rerender();
            });
        }
        if (room.localParticipant.isCameraEnabled) {
          void room.localParticipant
            .setCameraEnabled(false)
            .catch(() => {
              // Best effort, same as the microphone above.
            })
            .finally(() => {
              rerender();
            });
        }
      }
      showToast(t('webinar.speak_revoked_toast'));
      return;
    }
    let cancelled = false;
    void (async () => {
      if (speakFlowBusyRef.current) {
        return;
      }
      speakFlowBusyRef.current = true;
      // The grant unlocked the toggles, but the publishing Room does not exist yet: hold the
      // toggles for the whole mint + handover window, not just the Room swap itself.
      mediaHandoverBusyRef.current = true;
      try {
        const token = renewableTokenRef.current;
        if (token === null) {
          throw new Error('cannot mint a speak token without a join token');
        }
        const fresh = await fetchSpeakToken(slug, token);
        if (cancelled) {
          return;
        }
        // The publishing token renews the renewable one: later remints keep working, and the
        // proactive-renewal window follows it.
        renewableTokenRef.current = fresh.token;
        tokenWindowRef.current = credentialsWindow(fresh);
        await reconnectMedia(fresh, { microphone: true, camera: false });
        showToast(t('webinar.speak_granted_toast'));
      } catch {
        if (!cancelled) {
          showToast(t('webinar.speak_token_failed'));
        }
      } finally {
        speakFlowBusyRef.current = false;
        mediaHandoverBusyRef.current = false;
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [
    amSpeakGranted,
    joinIsViewer,
    mediaIsViewer,
    phase,
    slug,
    reconnectMedia,
    showToast,
    t,
    rerender,
  ]);

  // The mid-room self role change (webinar rooms, ADR 0010): a promotion into the
  // staff remints the join token once - the control service re-resolves the stored role into
  // publish grants - and hands the media session over, re-publishing exactly what the user
  // kept enabled (a viewer session publishes nothing: the toggles unlock instead). A demotion
  // mirrors the speak revoke: the microphone and camera go off (a live speak grant keeps the
  // floor) and the controls re-lock while the old token still lives. Meeting rooms keep full
  // media rights for plain participants, so roles never touch media there.
  useEffect(() => {
    if (phase !== 'connected') {
      return;
    }
    const isStaff = canModerate(ownRole);
    const wasStaff = wasOwnStaffRef.current;
    wasOwnStaffRef.current = isStaff;
    if (wasStaff === null || isStaff === wasStaff) {
      return;
    }
    if (!webinarMode) {
      return;
    }
    if (!isStaff) {
      setMediaIsViewer(true);
      if (!amSpeakGranted) {
        const room = roomRef.current;
        if (room !== null) {
          // The demotion mutes the microphone and turns the camera off locally, the way the
          // speak revoke does; the stale publish token dies at its TTL.
          if (room.localParticipant.isMicrophoneEnabled) {
            localMicIntentRef.current = true;
            void room.localParticipant
              .setMicrophoneEnabled(false)
              .catch(() => {
                // Best effort: the controls re-lock regardless.
              })
              .finally(() => {
                localMicIntentRef.current = false;
                rerender();
              });
          }
          if (room.localParticipant.isCameraEnabled) {
            void room.localParticipant
              .setCameraEnabled(false)
              .catch(() => {
                // Best effort, same as the microphone above.
              })
              .finally(() => {
                rerender();
              });
          }
        }
      }
      showToast(t('webinar.role_demoted_toast'));
      return;
    }
    if (roleRemintBusyRef.current) {
      // A remint is already in flight (role frames may repeat); its success path unlocks.
      return;
    }
    let cancelled = false;
    void (async () => {
      roleRemintBusyRef.current = true;
      // Same toggle hold as the speak handover: the Room swap must not race a publish.
      mediaHandoverBusyRef.current = true;
      try {
        // The media session needs the handover only when its token is still viewer-shaped:
        // a meeting-token holder (or an upgraded speaker) already publishes.
        const room = roomRef.current;
        const renewable = renewableTokenRef.current;
        if (renewable !== null && isViewerJoinToken(renewable)) {
          const fresh = await renewJoinCredentials();
          if (cancelled) {
            return;
          }
          await reconnectMedia(fresh, {
            microphone: room?.localParticipant.isMicrophoneEnabled ?? false,
            camera: room?.localParticipant.isCameraEnabled ?? false,
          });
        }
        setMediaIsViewer(false);
        showToast(t('webinar.role_promoted_toast'));
      } catch {
        if (!cancelled) {
          showToast(t('webinar.role_promote_failed'));
        }
      } finally {
        roleRemintBusyRef.current = false;
        mediaHandoverBusyRef.current = false;
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [
    ownRole,
    phase,
    webinarMode,
    amSpeakGranted,
    renewJoinCredentials,
    reconnectMedia,
    showToast,
    t,
    rerender,
  ]);

  // Removal (WS 4403) tears the media session down too; the removed screen replaces everything.
  useEffect(() => {
    if (!realtime.removed) {
      return;
    }
    leaveIntentRef.current = true;
    void roomRef.current?.disconnect();
  }, [realtime.removed]);

  // The staff end-for-everyone decision (the unified room bars) tears the media session down
  // the same way: the SFU room is deleted server-side behind the meeting_ended frame, and the
  // ended screen replaces everything.
  useEffect(() => {
    if (realtime.meetingEnded === null) {
      return;
    }
    leaveIntentRef.current = true;
    void roomRef.current?.disconnect();
  }, [realtime.meetingEnded]);

  // The unified room header's ping chip: the prejoin control-plane probe re-run on an
  // interval while the session is connected; a left session clears the last reading.
  useEffect(() => {
    if (phase !== 'connected') {
      setPingMs(null);
      return;
    }
    let cancelled = false;
    const probe = (): void => {
      void measureControlRtt().then((rtt) => {
        if (!cancelled) {
          setPingMs(rtt);
        }
      });
    };
    probe();
    const timer = setInterval(probe, RTT_PROBE_INTERVAL_MS);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [phase]);

  // A lobby rejection (CR-14: the 4407 deny or the 4400 timeout) tears the media session down
  // the same way: the gate refused this join, so the rejected client must not linger in the
  // SFU behind the rejected screen (camera and microphone stay live otherwise). The «Try
  // again» path starts a completely fresh session. A held join has no media session since
  // CR-15 - the roomRef disconnect is a null no-op there, the gate never let it connect.
  useEffect(() => {
    if (realtime.lobby !== 'denied' && realtime.lobby !== 'timeout') {
      return;
    }
    leaveIntentRef.current = true;
    void roomRef.current?.disconnect();
  }, [realtime.lobby]);

  // Reconcile the optimistic hand flip with the server-confirmed state. Before the first join
  // response (and the state frame) both sources are null: no own presence to reconcile against.
  const serverHand =
    ownIdentity !== null ? (realtime.participants.get(ownIdentity)?.handRaised ?? false) : false;
  useEffect(() => {
    if (optimisticHand !== null && (serverHand === optimisticHand || realtime.health !== 'live')) {
      setOptimisticHand(null);
    }
  }, [optimisticHand, serverHand, realtime.health]);
  const handRaised = optimisticHand ?? serverHand;

  /** Flips the own hand state: optimistic UI first, then the WS hand frame. */
  const toggleHand = useCallback((): void => {
    const next = !handRaised;
    setOptimisticHand(next);
    realtimeActions.sendHand(next);
  }, [handRaised, realtimeActions]);

  // Clear floater, toast and pill timers on unmount so no callback fires into an unmounted page.
  useEffect(() => {
    const timers = floaterTimersRef.current;
    return () => {
      for (const timer of timers) {
        clearTimeout(timer);
      }
      timers.clear();
      if (toastTimerRef.current !== null) {
        clearTimeout(toastTimerRef.current);
      }
      if (pillTimerRef.current !== null) {
        clearTimeout(pillTimerRef.current);
      }
    };
  }, []);

  // Chat unread counter: while the chat panel is open every arriving frame is read
  // immediately; while it is closed the frames past the last-read id accumulate into the
  // badge count, which resets on open. A fresh authoritative history window (connect,
  // reconnect, load-older is a plain append) always counts as read, so persisted rows never
  // show up as unread noise - only live frames appended between windows badge the button.
  const historySeqRef = useRef(0);
  useEffect(() => {
    if (historySeqRef.current !== realtime.historySeq) {
      historySeqRef.current = realtime.historySeq;
      lastReadMessageIdRef.current = realtime.messages.at(-1)?.id ?? 0;
      if (panel === 'chat') {
        setUnreadChat(0);
      }
      return;
    }
    if (panel === 'chat') {
      const openLatest = realtime.messages.at(-1)?.id ?? 0;
      if (openLatest > lastReadMessageIdRef.current) {
        lastReadMessageIdRef.current = openLatest;
      }
      setUnreadChat(0);
      return;
    }
    const arrived = realtime.messages.filter(
      (message) => message.id > lastReadMessageIdRef.current,
    ).length;
    if (arrived > 0) {
      lastReadMessageIdRef.current = realtime.messages.at(-1)?.id ?? lastReadMessageIdRef.current;
      setUnreadChat((current) => current + arrived);
    }
  }, [panel, realtime.historySeq, realtime.messages]);

  // Fullscreen state mirrors the browser so the «Ещё» menu item can flip its label.
  useEffect(() => {
    const onFullscreenChange = (): void => {
      setIsFullscreen(Boolean(document.fullscreenElement));
    };
    document.addEventListener('fullscreenchange', onFullscreenChange);
    return () => {
      document.removeEventListener('fullscreenchange', onFullscreenChange);
    };
  }, []);

  /** Toggles fullscreen on the room root (the «Ещё» menu item). */
  const handleToggleFullscreen = useCallback((): void => {
    const root = rootRef.current;
    if (root === null) {
      return;
    }
    if (document.fullscreenElement) {
      void document.exitFullscreen().catch(() => {
        // Fullscreen exits are best-effort; nothing to surface.
      });
      return;
    }
    if (typeof root.requestFullscreen === 'function') {
      void root.requestFullscreen().catch(() => {
        // The browser may refuse (permissions, iframe); nothing to surface.
      });
    }
  }, []);

  /**
   * Ends the meeting for everyone (moderators; the unified room bars): one `end` moderate
   * frame - the hub broadcasts meeting_ended to every member, closes the connections with
   * 4406 and deletes the SFU room. The own meeting_ended echo lands the actor on the same
   * post-room screen as everyone else, so no local teardown choreography is needed here.
   */
  const handleEndForAll = useCallback(async (): Promise<void> => {
    if (endingForAll || ownIdentity === null) {
      return;
    }
    setEndingForAll(true);
    realtimeActions.sendModerate('end', '');
  }, [endingForAll, ownIdentity, realtimeActions]);

  // Meeting hotkeys (Syncra): M/V/H/G and the RU-layout ь/м/р/п run the real toggles and
  // fire the feedback pill; suppressed while a dialog is open or a text field has focus.
  useEffect(() => {
    if (phase !== 'connected') {
      return;
    }
    function isEditableTarget(target: EventTarget | null): boolean {
      return (
        target instanceof HTMLInputElement ||
        target instanceof HTMLTextAreaElement ||
        target instanceof HTMLSelectElement ||
        (target instanceof HTMLElement && target.isContentEditable)
      );
    }
    function handler(event: KeyboardEvent): void {
      if (event.ctrlKey || event.metaKey || event.altKey) {
        return;
      }
      if (isEditableTarget(event.target)) {
        return;
      }
      if (inviteOpen || shortcutsOpen) {
        return;
      }
      const room = roomRef.current;
      if (room === null) {
        return;
      }
      const key = event.key.toLowerCase();
      const keyLabel = key.toUpperCase();
      // handleToggleMic/handleToggleCam are stable ref-based handlers; they are intentionally
      // kept out of the dependency list to avoid re-subscribing on every render. Both branches
      // check the publish admission first: a locked webinar viewer pressing M/V must not fire
      // the real toggles (the viewer-gated Room answers with the publish-permission error -
      // the exact webinar bug report) nor the feedback pill; the keys mirror the disabled
      // buttons, which stay inert.
      if (key === 'm' || key === 'ь') {
        if (!mayPublishMedia(room)) {
          return;
        }
        const enabling = !room.localParticipant.isMicrophoneEnabled;
        void handleToggleMic();
        showPill(
          t(enabling ? 'controls.pill_mic_on' : 'controls.pill_mic_off', { key: keyLabel }),
          enabling ? 'mic-on' : 'mic-off',
        );
      } else if (key === 'v' || key === 'м') {
        if (!mayPublishMedia(room)) {
          return;
        }
        const enabling = !room.localParticipant.isCameraEnabled;
        void handleToggleCam();
        showPill(
          t(enabling ? 'controls.pill_cam_on' : 'controls.pill_cam_off', { key: keyLabel }),
          enabling ? 'cam-on' : 'cam-off',
        );
      } else if (key === 'h' || key === 'р') {
        const next = !handRaised;
        setOptimisticHand(next);
        realtimeActions.sendHand(next);
        showPill(
          t(next ? 'controls.pill_hand_up' : 'controls.pill_hand_down', { key: keyLabel }),
          'hand',
        );
      }
    }
    window.addEventListener('keydown', handler);
    return () => {
      window.removeEventListener('keydown', handler);
    };
  }, [phase, inviteOpen, shortcutsOpen, handRaised, showPill, t, realtimeActions]);

  // A new recording re-arms the «being recorded» banner: the dismissal belongs to exactly one
  // recording, and a recording that ends and starts again informs again.
  const recording = realtime.recording;
  const recordingId = recording?.recordingId ?? null;
  useEffect(() => {
    setRecBannerDismissedId(null);
  }, [recordingId]);

  // The room object is mutable external state; every room event triggers a re-derivation.
  // Events are registered on the room synchronously in handleJoin, before connect(), so that
  // participants already in the room are not missed.

  // The meeting timer ticks once a second while the room is connected.
  useEffect(() => {
    if (phase !== 'connected') {
      return;
    }
    const timer = window.setInterval(() => {
      setElapsedSeconds((seconds) => seconds + 1);
    }, 1000);
    return () => {
      window.clearInterval(timer);
    };
  }, [phase]);

  // Disconnect on unload (pagehide also covers bfcache freezes) and on unmount. All local
  // teardown paths mark the intent first so the Disconnected event reads as a clean leave,
  // not a dropped connection.
  useEffect(() => {
    function onPageHide(): void {
      leaveIntentRef.current = true;
      disposeBackground();
      void roomRef.current?.disconnect();
    }
    window.addEventListener('pagehide', onPageHide);
    return () => {
      window.removeEventListener('pagehide', onPageHide);
      leaveIntentRef.current = true;
      disposeBackground();
      void roomRef.current?.disconnect();
    };
  }, []);

  async function handleJoin(request: PrejoinJoinRequest): Promise<void> {
    setSsoNotice(null);
    const sso = request.ssoToken ?? null;
    const check = checkDisplayName(request.displayName);
    if (check.error !== null && sso === null) {
      // The display name is required for the guest path only: an SSO join's name is
      // server-derived from the verified token.
      setError(t(`prejoin.name_error_${check.error}`));
      return;
    }
    setError(null);
    // Remember the chosen devices, options (and the name) so the next prejoin preselects them.
    // The update goes through the store so the in-memory cache stays consistent; the audio
    // effect switches default to on for join requests that predate the effects dialog.
    devicePrefsStore().update({
      cameraDeviceId: request.cameraDeviceId,
      micDeviceId: request.micDeviceId,
      displayName: check.value,
      speakerDeviceId: request.speakerDeviceId ?? null,
      // The authoritative final write of the remembered entry state: whatever the prejoin
      // toggles read at join time is the next join's default (the toggles also write on
      // every change; this covers paths like a device disappearing mid-prejoin).
      joinMicOn: request.micDeviceId !== null,
      joinCamOn: request.cameraDeviceId !== null,
      mirrorVideo: request.mirrorVideo ?? true,
      bgMode: request.backgroundMode ?? 'blur',
      bgQuality: request.backgroundQuality ?? 'auto',
      resolvedQuality: request.backgroundResolvedQuality ?? null,
      blurStrength: request.backgroundBlurStrength ?? 'medium',
      videoFilter: request.backgroundVideoFilter ?? 'none',
      mask: request.backgroundMask ?? 'none',
      micEchoCancellation: request.micEchoCancellation ?? true,
      micNoiseSuppression: request.micNoiseSuppression ?? true,
      micAutoGainControl: request.micAutoGainControl ?? true,
    });
    // The session's own request: the in-call mic re-acquires read the device id and switches.
    joinMediaRef.current = request;
    // The lobby-retry seed: same devices and name, never the handed-over background (its
    // processor dies with this session; the retry joins with a raw capture).
    lastJoinRequestRef.current = { ...request, background: undefined };
    // The processed camera (if any) is now the room's to publish and to dispose.
    backgroundRef.current = request.background ?? null;
    // Seed the machine with the session's ORIGINAL camera capture: without it, the first
    // in-call switch could mistake the published canvas track for the capture and feed
    // the composite back into itself. A raw join clears it - the machine then adopts the
    // SDK capture from the publication.
    getCameraFx().seedCapture(request.background?.cameraTrack ?? null);
    setSpeakerSinkId(request.speakerDeviceId ?? null);
    setMirrorVideo(request.mirrorVideo ?? true);
    setPhase('joining');
    // Per-join reset: speakers, pin, drop reason, panel and the timer belong to the previous
    // session; so do the Syncra surfaces (dialogs, pill, unread counter, quality, fullscreen
    // state stays browser-owned).
    leaveIntentRef.current = false;
    if (mediaRecoveryTimerRef.current !== null) {
      clearTimeout(mediaRecoveryTimerRef.current);
      mediaRecoveryTimerRef.current = null;
    }
    mediaRecoveryAttemptRef.current = 0;
    mediaRecoveryHoldSinceRef.current = 0;
    mediaRecoveryReasonRef.current = null;
    tokenWindowRef.current = null;
    // The previous attempt's admit credentials belong to the previous session (a retry after a
    // deny or a fresh join re-asks the lobby from zero).
    setAdmittedMedia(null);
    setDropReason(null);
    setLifecycleState('none');
    setActiveSpeakers([]);
    setPinnedKey(null);
    setPanel(null);
    setElapsedSeconds(0);
    setConnPhase('disconnected');
    setDisplayName(check.value);
    setOptimisticHand(null);
    setReactionsOpen(false);
    setFloaters([]);
    setInviteOpen(false);
    setInvitesOpen(false);
    setShortcutsOpen(false);
    setPill(null);
    setUnreadChat(0);
    lastReadMessageIdRef.current = 0;
    setLocalQuality('unknown');
    setEndingForAll(false);
    setOwnUpvotes(new Set());
    setSpeakBusyIds(new Set());
    setBoardBusyIds(new Set());
    boardSyncHubRef.current?.reset();
    boardPointerListenersRef.current.clear();
    setWebinarPanelOpen(false);
    wasSpeakGrantedRef.current = false;
    speakReconnectingRef.current = false;
    speakFlowBusyRef.current = false;
    roleRemintBusyRef.current = false;
    mediaHandoverBusyRef.current = false;
    wasOwnStaffRef.current = null;
    setSsoToken(sso);
    // Camera publish path (H264 simulcast vs VP9 SVC vs VP8 simulcast) and the session
    // publish tier are decided once per join: the path from the browser's video send
    // capabilities, the tier from the machine's hardware signals plus the stored
    // camQuality override - with the effects-path rule (an auto-'high' session that starts
    // with an active treatment runs at medium; see resolveSessionTier). Unknown
    // capabilities fall back to VP8 simulcast.
    const publishDecision = decidePublishPath(detectVideoSendCapabilities());
    publishDecisionRef.current = publishDecision;
    publishTierRef.current = resolveSessionTier(
      detectHardwareSignal(),
      devicePrefsStore().get().camQuality,
      joinMediaRef.current !== null && sessionEffectsActive(joinMediaRef.current),
    );
    setPublishMode(publishDecision.mode);
    try {
      const credentials =
        sso !== null
          ? await joinWithSsoRecovery(sso)
          : await joinRoom(slug, guestJoinBody(check.value, inviteTokenRef.current));
      setCredentials(credentials);
      // This token is what the re-mint path presents on the first reconnect, and its validity
      // window is the baseline of the proactive renewal (CR-13). A held join's deferred token
      // (CR-15) authenticates the control WS the same way - only the SFU refuses it.
      renewableTokenRef.current = credentials.token;
      tokenWindowRef.current = credentialsWindow(credentials);
      // The server-assigned identity (guest-<16 hex> for guests, kc-<sub> for SSO) is the
      // authoritative own presence; the role seeds it until the WS state frame takes over.
      setIdentity(credentials.identity);
      setJoinedRole(credentials.role);
      if (credentials.inviteRejected !== undefined) {
        // CR-20: the presented invitation was refused (revoked, expired, exhausted, unknown -
        // the wire text cannot distinguish). The join itself proceeds on the plain guest path,
        // so a neutral toast is all the surface this needs; a lobbied room keeps holding.
        showToast(t('room.invite_rejected_toast'));
      }
      if (credentials.mediaDeferred) {
        // CR-15: the join was held - NO media session exists. The handed-over background
        // processor dies here (its camera capture must not sit live for the whole wait; the
        // admit publishes a fresh capture), the realtime channel connects with the deferred
        // token and the waiting screen takes over until the admit delivers the credentials.
        disposeBackground();
        setConnPhase('disconnected');
        setPhase('connected');
        return;
      }
      const room = new Room(ROOM_OPTIONS);
      registerRoomEvents(room, buildRoomHandlers());
      roomRef.current = room;
      // A webinar viewer token cannot publish: the join skips every publish attempt
      // (the SFU would reject them) and the handed-over processed camera is released - its
      // input capture must not stay live unused.
      const viewerJoin = isViewerJoinToken(credentials.token);
      // The publish gating UI follows the CURRENT media session's token from here on (the
      // promotion handover and the demotion re-lock flip it mid-room).
      setMediaIsViewer(viewerJoin);
      if (viewerJoin) {
        disposeBackground();
      }
      const warnings = await connectAndPublish(
        room,
        credentials.url,
        credentials.token,
        turnConnectOptions(credentials.turn),
        request,
        publishDecision,
        publishTierRef.current,
        backgroundRef.current,
        detailOf,
        viewerJoin,
      );
      setMediaWarnings(warnings);
      setConnPhase('connected');
      setPhase('connected');
    } catch (err: unknown) {
      // SSO join rejections keep the prejoin usable: a notice explains what happened and the
      // guest path remains one click away. sso_invalid also drops the (now expired) session.
      const noticeKey = err instanceof JoinApiError ? ssoJoinNoticeKey(err.code) : null;
      if (noticeKey !== null) {
        if (err instanceof JoinApiError && err.code === 'sso_invalid') {
          const storage = browserSsoStorage();
          if (storage !== null) {
            clearSsoSession(storage);
          }
          setSsoToken(null);
        }
        dropToPrejoin(t(noticeKey));
        return;
      }
      leaveIntentRef.current = true;
      disposeBackground();
      void roomRef.current?.disconnect();
      roomRef.current = null;
      renewableTokenRef.current = null;
      tokenWindowRef.current = null;
      setConnPhase('disconnected');
      // CR-26: the room lifecycle outcomes get their own screens (unknown / expired) instead of
      // the generic failure alert inside the prejoin card.
      setLifecycleState(lifecycleStateOf(err));
      setPhase('failed');
      setError(detailOf(err));
    }
  }

  /**
   * The lobby admit landed (CR-15): the lobby_admitted frame's media credentials connect the
   * SFU and enter the room - the same publish path as the direct join, with the remembered
   * prejoin request (devices, toggles) and a fresh capture (the handed-over background
   * processor died when the hold started). Declared as a hoisted function: the realtime hook's
   * stable callback below dispatches into it through a ref-free closure.
   */
  async function handleLobbyAdmittedImpl(media: LobbyAdmittedMedia): Promise<void> {
    // Duplicates cannot happen protocol-side (one admit per held connection), but a late frame
    // after a local teardown must not build a second session.
    if (roomRef.current !== null) {
      return;
    }
    // The admitted token becomes the renewable one and the proactive-renewal baseline (CR-13).
    renewableTokenRef.current = media.token;
    tokenWindowRef.current = tokenWindowFromExpiresAt(media.expiresAt);
    // Flips mediaHeld off: the waiting screen unmounts and the room renders while the media
    // connects (the connection chip reads «connecting» until the SFU answers).
    setAdmittedMedia(media);
    const request = joinMediaRef.current;
    if (request === null) {
      // Unreachable through the normal flow (the join stored the request before the hold);
      // fail the session visibly instead of waiting forever.
      setConnPhase('disconnected');
      setPhase('failed');
      setError(detailOf(new Error('admit arrived without the join request')));
      return;
    }
    // Camera publish path and session tier decided per media session, like the direct join
    // (the effects-path rule reads the SAME stored join request - the admit joins with the
    // treatment the prejoin handed over).
    const publishDecision = decidePublishPath(detectVideoSendCapabilities());
    publishDecisionRef.current = publishDecision;
    publishTierRef.current = resolveSessionTier(
      detectHardwareSignal(),
      devicePrefsStore().get().camQuality,
      sessionEffectsActive(request),
    );
    setPublishMode(publishDecision.mode);
    const room = new Room(ROOM_OPTIONS);
    registerRoomEvents(room, buildRoomHandlers());
    roomRef.current = room;
    const viewerJoin = isViewerJoinToken(media.token);
    setMediaIsViewer(viewerJoin);
    try {
      const warnings = await connectAndPublish(
        room,
        media.url,
        media.token,
        turnConnectOptions(media.turn),
        request,
        publishDecision,
        publishTierRef.current,
        backgroundRef.current,
        detailOf,
        viewerJoin,
      );
      setMediaWarnings(warnings);
      setConnPhase('connected');
    } catch (err: unknown) {
      // The admit delivered credentials the SFU refused (or the SFU is unreachable): the join
      // completed on the control plane, but the session has no media - land on the failed
      // screen like a failed direct join.
      leaveIntentRef.current = true;
      disposeBackground();
      void roomRef.current?.disconnect();
      roomRef.current = null;
      setConnPhase('disconnected');
      setPhase('failed');
      setError(detailOf(err));
    }
  }

  /**
   * Releases the handed-over background processor and its input camera track, plus the
   * in-call machine's current generation (idempotent; leave paths and media handovers).
   * The machine stays usable: the next apply re-resolves the capture from whatever the
   * fresh session publishes.
   */
  function disposeBackground(): void {
    getCameraFx().dispose();
    backgroundRef.current?.dispose();
    backgroundRef.current = null;
  }

  function handleLeave(): void {
    leaveIntentRef.current = true;
    void roomRef.current?.disconnect();
  }

  /**
   * Returns from the dropped state to the prejoin screen (same room, remembered devices and
   * display name) without a page reload; the next Join starts a fresh session. Deliberately
   * not an auto-reconnect loop: the user decides when to come back.
   */
  function handleRejoin(): void {
    dropToPrejoin(null);
  }

  /**
   * The lobby waiting screen's cancel: the same clean leave as the room's own leave button.
   * The media session tears down, the realtime client stops with it and the page lands on the
   * «You left the room» state. A held join (CR-15) has no media session: no Disconnected event
   * would ever fire, so the cancel ends the held session directly - the phase flip stops the
   * realtime channel through the hook's enabled gate.
   */
  function handleLobbyCancel(): void {
    if (roomRef.current === null) {
      leaveIntentRef.current = true;
      disposeBackground();
      setConnPhase('disconnected');
      setPhase('left');
      return;
    }
    handleLeave();
  }

  /**
   * The lobby-rejected screen's «Try again» (CR-14): tear this attempt down and start a fresh
   * join from the remembered request - a new join POST, a new lobby hold. No automatic loop:
   * the user re-asks deliberately. The handed-over background processor is never reused (it
   * died with the denied session), so the retry joins with a raw capture; the current SSO
   * token wins over the one stored in the request (it may have rotated since).
   */
  function handleLobbyRetry(): void {
    const request = lastJoinRequestRef.current;
    dropToPrejoin(null);
    if (request === null) {
      return;
    }
    const sso = ssoToken ?? request.ssoToken;
    void handleJoin({
      ...request,
      background: undefined,
      ...(sso !== null ? { ssoToken: sso } : {}),
    });
  }

  /**
   * Toggles the local screen share. The share state is derived from the local
   * publications (isScreenSharing), so every stop path converges here: the button,
   * the browser's native stop bar (the SDK listens for the track's `ended` event and
   * unpublishes screen shares itself) and leaving the room (disconnect unpublishes
   * everything). Options are decided per toggle from the same capability matrix the
   * camera path reads; stopping passes no options.
   */
  async function handleShareScreen(): Promise<void> {
    const room = roomRef.current;
    if (room === null) {
      return;
    }
    const starting = !isScreenSharing(room.localParticipant);
    setScreenError(null);
    setScreenBusy(true);
    try {
      await room.localParticipant.setScreenShareEnabled(
        starting,
        starting ? screenShareCaptureOptions() : undefined,
        starting
          ? screenSharePublishOptions(decideScreenSharePath(detectVideoSendCapabilities()))
          : undefined,
      );
    } catch (err: unknown) {
      // Cancelling the browser picker is a normal outcome, not an error to surface.
      if (!isScreenShareCancellation(err)) {
        setScreenError(t('room.screen_share_failed', { detail: detailOf(err) }));
      }
    } finally {
      setScreenBusy(false);
      rerender();
    }
  }

  /**
   * The media-toggle admission check shared by the buttons and the hotkeys: publishing is
   * allowed only while the CURRENT media session actually holds the publish grant. Three
   * guards, cheapest first: a media handover in flight (the Room behind roomRef is stale or
   * mid-connect - a publish attempt answers with "failed to publish track, insufficient
   * permissions" from the old viewer room, the exact toast the webinar bug report carries);
   * the UI-level grant gate (locked webinar viewer); and the SDK's own permission mirror as
   * the last line of defense (defense in depth against any future UI-state drift - the SFU
   * would reject the publish anyway).
   */
  function mayPublishMedia(room: Room): boolean {
    if (mediaHandoverBusyRef.current) {
      return false;
    }
    if (!canPublishNowRef.current) {
      return false;
    }
    return room.localParticipant.permissions?.canPublish !== false;
  }

  /**
   * Toggles the local microphone (control-pill button). Toggling passes no capture or publish
   * options when muting; unmuting captures with the CURRENT effect switches (so a switch made
   * while no track was live applies on the next acquisition) and the remembered device.
   */
  async function handleToggleMic(): Promise<void> {
    const room = roomRef.current;
    if (room === null || !mayPublishMedia(room)) {
      return;
    }
    const enabling = !room.localParticipant.isMicrophoneEnabled;
    setControlError(null);
    // The mute the SDK performs for this toggle must not read as a moderator mute; the flag
    // covers exactly the await window in which the SDK fires TrackMuted.
    localMicIntentRef.current = true;
    try {
      await room.localParticipant.setMicrophoneEnabled(
        enabling,
        enabling
          ? microphoneCaptureOptions(
              joinMediaRef.current?.micDeviceId ?? null,
              micEffectsRef.current,
            )
          : undefined,
        enabling ? microphonePublishOptions() : undefined,
      );
    } catch (err: unknown) {
      setControlError(t('room.mic_toggle_failed', { detail: detailOf(err) }));
    } finally {
      localMicIntentRef.current = false;
      refreshMicEffectsEffective();
      rerender();
    }
  }

  /**
   * Toggles the local camera. The re-enable passes the SESSION capture and publish
   * options (tier resolution + encoding): an unconstrained default capture would come
   * back at a different size than the session tier - and than the effects processor's
   * canvas - so a camera off/on cycle would renegotiate the sender and stall remote
   * subscribers (the dimension-changing replaceTrack failure mode).
   */
  async function handleToggleCam(): Promise<void> {
    const room = roomRef.current;
    if (room === null || !mayPublishMedia(room)) {
      return;
    }
    const enabling = !room.localParticipant.isCameraEnabled;
    setControlError(null);
    try {
      const decision = publishDecisionRef.current;
      const deviceId = joinMediaRef.current?.cameraDeviceId ?? null;
      if (enabling && decision !== null) {
        const tier = publishTierRef.current;
        await room.localParticipant.setCameraEnabled(
          true,
          cameraCaptureOptions(deviceId, tier),
          cameraPublishOptions(decision, tier),
        );
      } else {
        await room.localParticipant.setCameraEnabled(enabling);
      }
      // Effects chosen (or changed) while the camera was off - or reset by a media
      // handover - apply to the freshly published capture here; with nothing active
      // the machine call is a no-op.
      if (enabling) {
        await getCameraFx().apply(effectsRequestFromPrefs(devicePrefsStore().get()));
      }
    } catch (err: unknown) {
      setControlError(t('room.cam_toggle_failed', { detail: detailOf(err) }));
    } finally {
      rerender();
    }
  }

  /**
   * Quick-settings microphone device switch: the preference and the session request update
   * first (the next acquisition captures the new device even when nothing is live), then a
   * live published track re-acquires through the SDK's restartTrack - the same re-acquisition
   * the microphone effects switch falls back to, now with the deviceId as the payload. The
   * capture options are the shared microphoneCaptureOptions(deviceId, effects) mapping the
   * toggles use; null means the SDK's default device, never "no microphone".
   */
  async function handleMicDeviceChange(deviceId: string | null): Promise<void> {
    devicePrefsStore().update({ micDeviceId: deviceId });
    const request = joinMediaRef.current;
    if (request !== null) {
      joinMediaRef.current = { ...request, micDeviceId: deviceId };
    }
    const room = roomRef.current;
    if (room === null || mediaHandoverBusyRef.current) {
      return;
    }
    const localTrack = room.localParticipant.getTrackPublication(
      Track.Source.Microphone,
    )?.audioTrack;
    if (localTrack === undefined) {
      return; // nothing live (muted join, viewer): the next unmute picks the stored device
    }
    setControlError(null);
    try {
      await localTrack.restartTrack(microphoneCaptureOptions(deviceId, micEffectsRef.current));
    } catch (err: unknown) {
      setControlError(t('room.device_switch_failed', { detail: detailOf(err) }));
    } finally {
      // The re-acquisition replaced the underlying capture: the effects dialog's live effective
      // badge and the dev e2e bridge must read the NEW track, not the replaced one.
      refreshMicEffectsEffective();
      rerender();
    }
  }

  /**
   * Quick-settings camera device switch: preference and session request first, then - only
   * when the camera is actually live and this client may publish - the SAME off/on cycle the
   * camera toggle uses (same session tier, so the published dimensions stay stable; the
   * effects machine re-applies onto the fresh capture). Viewers and camera-off sessions stay
   * store-only: the next enable captures the remembered device.
   */
  async function handleCameraDeviceChange(deviceId: string | null): Promise<void> {
    devicePrefsStore().update({ cameraDeviceId: deviceId });
    const request = joinMediaRef.current;
    if (request !== null) {
      joinMediaRef.current = { ...request, cameraDeviceId: deviceId };
    }
    const room = roomRef.current;
    if (room === null || !canPublishNowRef.current || mediaHandoverBusyRef.current) {
      return;
    }
    if (!room.localParticipant.isCameraEnabled) {
      return;
    }
    setControlError(null);
    try {
      const decision = publishDecisionRef.current;
      const tier = publishTierRef.current;
      if (decision !== null) {
        await room.localParticipant.setCameraEnabled(false);
        await room.localParticipant.setCameraEnabled(
          true,
          cameraCaptureOptions(deviceId, tier),
          cameraPublishOptions(decision, tier),
        );
        await getCameraFx().apply(effectsRequestFromPrefs(devicePrefsStore().get()));
      }
    } catch (err: unknown) {
      setControlError(t('room.device_switch_failed', { detail: detailOf(err) }));
    } finally {
      rerender();
    }
  }

  /**
   * Quick-settings speaker switch: the stored preference plus the live sink id every AudioSink
   * reacts to (the setSinkId path; no re-acquisition exists for outputs).
   */
  function handleSpeakerDeviceChange(deviceId: string | null): void {
    devicePrefsStore().update({ speakerDeviceId: deviceId });
    setSpeakerSinkId(deviceId);
  }

  /**
   * Quick-settings camera quality: the stored publish override only. A pinned tier resolves on
   * the NEXT session (the session-tier invariant - no capture renegotiation mid-call), exactly
   * like the /settings page; the popover note says so.
   */
  function handleCamQualityChange(quality: CameraQualitySetting): void {
    devicePrefsStore().update({ camQuality: quality });
  }

  /** The quick-settings background pick: the store subscription applies it live in-call. */
  function handleBgModeChange(mode: BackgroundMode): void {
    devicePrefsStore().update({ bgMode: mode });
  }

  const room = roomRef.current;
  const connected = phase === 'connected' && room !== null;
  const tiles = connected ? deriveTiles(room.localParticipant, room.remoteParticipants) : [];
  const screenShares = connected
    ? deriveScreenShares(room.localParticipant, room.remoteParticipants)
    : [];
  const audioTracks = connected ? remoteAudioTracks(room.remoteParticipants) : [];
  const stageTiles = webinarStageTiles({
    tiles,
    shares: [],
    roles: presenceRoles,
    // Publish-capable, not merely speak-granted: staff publish by role, granted viewers by
    // their minted token - the stage shows the local tile whenever this client may publish
    // and the camera is actually live.
    ownPublishing: canPublishNow,
  });
  const sharing = connected && room !== null ? isScreenSharing(room.localParticipant) : false;
  const sharingIdentities = new Set(screenShares.map((share) => share.identity));
  /** Pin/unpin for a tile key (identity, or screen:<identity> for a share tile). */
  const togglePinFor = (key: string): void => {
    setPinnedKey((current) => togglePinned(current, key));
  };
  /** Admins carry the amber host shield on their tile nameplate. */
  const isAdminIdentity = (identity: string): boolean =>
    (realtime.participants.get(identity)?.role ?? 'participant') === 'admin';
  /** Live hand state per tile: the own optimistic flag merged with the presence channel. */
  const handRaisedFor = (identity: string): boolean =>
    identity === ownIdentity
      ? handRaised
      : (realtime.participants.get(identity)?.handRaised ?? false);
  const togglePanelFor = (target: PanelMode): void => {
    setPanel((current) => nextPanel(current, target));
  };
  const link = roomLink(window.location.origin, slug);

  /**
   * The quick-settings binding both control bars render the gear for (personal settings, every
   * role). Values mirror the preference store (the session request the join wrote); the
   * application paths live in the handlers above - the popover is a shortcut surface.
   */
  const quickSettings: QuickSettingsBinding | undefined = connected
    ? {
        micDeviceId: storedPrefs.micDeviceId,
        cameraDeviceId: storedPrefs.cameraDeviceId,
        speakerDeviceId: storedPrefs.speakerDeviceId,
        camQuality: storedPrefs.camQuality,
        bgMode: storedPrefs.bgMode,
        onMicDevice: (deviceId) => {
          void handleMicDeviceChange(deviceId);
        },
        onCameraDevice: (deviceId) => {
          void handleCameraDeviceChange(deviceId);
        },
        onSpeakerDevice: handleSpeakerDeviceChange,
        onCamQuality: handleCamQualityChange,
        onBgMode: handleBgModeChange,
        onOpenEffects: () => {
          setEffectsOpen(true);
        },
      }
    : undefined;

  if (!isValidRoomSlug(slug)) {
    return (
      <main className="flex min-h-screen flex-col items-center justify-center bg-background px-6 text-foreground">
        <div className="w-full max-w-md rounded-2xl border border-white/10 bg-zinc-900/60 p-6 text-center shadow-2xl shadow-black/50">
          <h1 className="text-xl font-semibold text-zinc-100">{t('room.invalid_address_title')}</h1>
          <p className="mt-2 text-sm text-zinc-400">{t('room.slug_rule')}</p>
          <p data-testid="conn-state" className="mt-4 font-mono text-xs text-zinc-500">
            {t('room.conn_disconnected')}
          </p>
        </div>
      </main>
    );
  }

  // Terminal moderation state: the media session has been torn down; no re-join here.
  if (realtime.removed) {
    return <RemovedScreen />;
  }

  // The staff end-for-everyone decision (the unified room bars): the same terminal family,
  // worded by who ended the room.
  if (realtime.meetingEnded !== null) {
    return (
      <EndedScreen
        by={realtime.meetingEnded.by}
        byName={realtime.meetingEnded.byName}
        ownIdentity={ownIdentity}
      />
    );
  }

  // Terminal lobby rejection (CR-14): the 4407 deny or the 4400 wait timeout replaces the room
  // regardless of the connection phase - the rejection's own teardown drives the phase to
  // «left», and the screen must outlive it (dropToPrejoin via the retry clears the flag).
  if (realtime.lobby === 'denied' || realtime.lobby === 'timeout') {
    const rejection: LobbyRejectionKind = realtime.lobby === 'denied' ? 'denied' : 'timeout';
    return <LobbyRejectedScreen kind={rejection} roomName={slug} onRetry={handleLobbyRetry} />;
  }

  // Lobby gating (CR-14, media deferred since CR-15): a held join shows the waiting screen and
  // has NO media session - the gate opens on the deferred credentials (the join response
  // carried none) and stays until the admit delivers the real ones (lobby_admitted). The
  // lobby==='waiting' arm keeps covering the window before the WS handshake answers (and a
  // legacy server whose hold still rides a connected media session).
  const mediaHeld = credentials?.mediaDeferred === true && admittedMedia === null;
  if (phase === 'connected' && (mediaHeld || realtime.lobby === 'waiting')) {
    // The toast overlay rides the waiting screen too: a refused invitation (CR-20) surfaces its
    // neutral rejection notice exactly where the held joiner is looking - fixed-positioned,
    // because the waiting screen owns the whole page.
    return (
      <>
        <LobbyWaitingScreen roomName={slug} onCancel={handleLobbyCancel} />
        {toast !== null ? (
          <div
            role="alert"
            data-testid="moderation-toast"
            className="fixed top-3 left-1/2 z-50 flex -translate-x-1/2 items-center gap-2 rounded-full border border-amber-500/40 bg-amber-500/15 px-4 py-1.5 text-xs font-medium text-amber-200 backdrop-blur"
          >
            <ShieldAlert className="h-3.5 w-3.5" />
            {toast}
          </div>
        ) : null}
      </>
    );
  }

  return (
    <main
      ref={rootRef}
      data-testid="layout-root"
      className="flex h-dvh flex-col bg-background text-foreground"
    >
      {/* Syncra top bar: slug badge, chips, timer + REC, layout toggle + signal chip. */}
      <header className="flex h-12 shrink-0 items-center gap-3 border-b border-white/[0.06] bg-zinc-950 px-3 sm:px-4">
        <h1
          data-testid="room-title"
          title={slug}
          className="hidden max-w-[40ch] truncate rounded-md border border-white/10 bg-white/[0.04] px-2 py-1 font-mono text-[11px] font-normal text-zinc-400 min-[420px]:block"
        >
          {slug}
        </h1>
        <span
          data-testid="conn-state"
          className={cn(
            'flex shrink-0 items-center gap-1.5 rounded-full border px-2 py-0.5 font-mono text-[11px]',
            CONN_CHIP_CLASSES[connPhase],
          )}
        >
          <span
            aria-hidden
            className={cn('h-1.5 w-1.5 rounded-full', CONN_DOT_CLASSES[connPhase])}
          />
          {t(`room.conn_${connPhase}`)}
        </span>
        {webinarMode ? (
          <>
            <span
              data-testid="live-badge"
              className="inline-flex shrink-0 items-center gap-1.5 rounded-md bg-red-500 px-2 py-1 text-[11px] font-bold tracking-wider text-white uppercase"
            >
              <span aria-hidden className="relative flex size-1.5">
                <span className="absolute inline-flex size-1.5 animate-ping rounded-full bg-white opacity-75" />
                <span className="relative inline-flex size-1.5 rounded-full bg-white" />
              </span>
              {t('webinar.live')}
            </span>
            <span
              data-testid="viewers-count"
              title={t('webinar.viewers_title')}
              className="inline-flex shrink-0 items-center gap-1.5 rounded-md bg-white/[0.05] px-2 py-1 text-[11.5px] font-medium tabular-nums text-zinc-300"
            >
              <Eye className="size-3.5 text-zinc-500" />
              {viewers}
            </span>
          </>
        ) : null}
        <Badge
          variant="outline"
          data-testid="participant-count"
          className="hidden border-white/10 bg-white/[0.04] font-mono text-[11px] font-normal text-zinc-400 sm:inline-flex"
        >
          {t('room.participants_in_room', { count: tiles.length })}
        </Badge>
        {connected && publishMode !== null && !isMobile && !webinarMode ? (
          <span
            data-testid="publish-mode"
            title={t('room.publish_mode_title')}
            className="hidden shrink-0 rounded bg-white/[0.04] px-1.5 py-0.5 font-mono text-[10px] text-zinc-500 lg:block"
          >
            {publishMode}
          </span>
        ) : null}
        {sharing ? (
          <span
            data-testid="sharing-indicator"
            title={t('room.sharing_title')}
            className="hidden shrink-0 items-center gap-1.5 rounded-full border border-emerald-500/40 bg-emerald-500/10 px-2 py-0.5 text-[11px] font-medium text-emerald-300 md:flex"
          >
            <span aria-hidden className="h-1.5 w-1.5 animate-pulse rounded-full bg-emerald-400" />
            {t('room.sharing_indicator')}
          </span>
        ) : null}

        {connected ? (
          <div className="mx-auto flex shrink-0 items-center gap-2 sm:gap-3">
            {!isMobile ? (
              <span
                data-testid="meeting-timer"
                title={t('room.timer_title')}
                className="inline-flex items-center gap-1.5 rounded-md bg-white/[0.05] px-2 py-1 text-[11.5px] font-medium tabular-nums text-zinc-300"
              >
                <Clock className="size-3 text-zinc-500" />
                {formatMeetingTimer(elapsedSeconds)}
              </span>
            ) : null}
            {/* The recording state lives in the BOTTOM bars now (the unified room bars): the
                pill carries it for meetings, the webinar bar for webinars; the red banner
                below stays the room-wide active-recording surface. */}
          </div>
        ) : (
          <div className="mx-auto" />
        )}

        {connected && !isMobile ? (
          <div className="hidden items-center gap-1 md:flex">
            {pingMs !== null ? (
              <span
                data-testid="ping-chip"
                title={t('room.ping_title')}
                className="mr-1 flex items-center gap-1.5 rounded-md bg-white/[0.04] px-2 py-1 text-[11px] font-medium tabular-nums text-zinc-400"
              >
                {t('room.ping_value', { ms: pingMs })}
              </span>
            ) : null}
            <span
              data-testid="quality-aggregate"
              title={t('room.quality_aggregate_title')}
              className="flex items-center gap-1.5 rounded-md bg-white/[0.04] px-2 py-1 text-[11px] font-medium text-zinc-400"
            >
              <SignalBars level={aggregateQualityLevel(qualityMap)} />
              {t('room.quality_aggregate')}
            </span>
            <div
              data-testid="signal-quality"
              title={t('room.signal_title')}
              className="flex items-center gap-1.5 rounded-md bg-white/[0.04] px-2 py-1 text-[11px] font-medium text-zinc-400"
            >
              <SignalBars level={signalLevel(localQuality)} />
              {signalLevel(localQuality) < 2 ? t('room.signal_weak') : t('room.signal_stable')}
            </div>
          </div>
        ) : null}
        <LangSwitch className={connected ? '' : 'ml-auto'} />
      </header>

      {connected &&
      recording !== null &&
      isActiveRecordingStatus(recording.state) &&
      recBannerDismissedId !== recording.recordingId ? (
        <div
          data-testid="rec-banner"
          role="status"
          className="flex h-8 shrink-0 items-center justify-center gap-2 border-b border-red-500/25 bg-red-500/10 px-4 text-xs font-medium text-red-200"
        >
          <Circle aria-hidden className="h-2.5 w-2.5 fill-red-400 text-red-400" />
          {t('recording.banner')}
          <button
            type="button"
            data-testid="rec-banner-dismiss"
            aria-label={t('recording.banner_dismiss_aria')}
            title={t('recording.banner_dismiss_aria')}
            onClick={() => {
              setRecBannerDismissedId(recording.recordingId);
            }}
            className="grid h-5 w-5 place-items-center rounded text-red-300 transition-colors hover:bg-red-500/20 hover:text-red-100"
          >
            <X className="h-3 w-3" />
          </button>
        </div>
      ) : null}

      {connected && webinarMode ? (
        <div className="relative flex min-h-0 flex-1">
          <div className="relative flex min-w-0 flex-1 flex-col">
            <div className="relative flex min-h-0 flex-1 flex-col">
              {toast !== null ? (
                <div
                  role="alert"
                  data-testid="moderation-toast"
                  className="absolute top-3 left-1/2 z-30 flex -translate-x-1/2 items-center gap-2 rounded-full border border-amber-500/40 bg-amber-500/15 px-4 py-1.5 text-xs font-medium text-amber-200 backdrop-blur"
                >
                  <ShieldAlert className="h-3.5 w-3.5" />
                  {toast}
                </div>
              ) : null}
              {connPhase === 'reconnecting' ? (
                <p
                  role="status"
                  data-testid="reconnecting-banner"
                  className="absolute top-3 left-1/2 z-30 -translate-x-1/2 rounded-full border border-amber-500/40 bg-amber-500/15 px-4 py-1.5 text-xs font-medium text-amber-200 backdrop-blur"
                >
                  {t('room.reconnecting_banner')}
                </p>
              ) : null}
              {screenError !== null ? (
                <p role="alert" className="px-4 pt-2 text-sm text-red-300">
                  {screenError}
                </p>
              ) : null}
              {controlError !== null ? (
                <p role="alert" className="px-4 pt-2 text-sm text-red-300">
                  {controlError}
                </p>
              ) : null}
              {boardBroadcastOpen ? (
                // The board broadcast surface (the board surface amendment): the staff opened
                // the board as the room's shared stage. EVERY member renders it - editors draw
                // (role or board grant, ADR 0013), everyone else watches read-only - and the
                // cameras collapse into the broadcast strip under the canvas (the same idiom
                // the share stage uses).
                <div className="flex min-h-0 flex-1 flex-col p-2 sm:p-3">
                  <div className="flex min-h-0 flex-1 flex-col" data-testid="board-broadcast">
                    <BoardPanel
                      roomId={slug}
                      isEditor={amBoardEditor}
                      status={realtime.health}
                      sendUpdate={sendBoardUpdate}
                      subscribeSync={subscribeBoardSync}
                      sendPointer={sendBoardPointer}
                      subscribePointers={subscribeBoardPointers}
                      labelFor={boardPointerLabelFor}
                      variant="surface"
                      className="min-h-0 flex-1"
                    />
                  </div>
                  {stageTiles.length > 0 ? (
                    <div
                      data-testid="broadcast-camera-strip"
                      aria-label={t('room.share_strip_aria')}
                      className="scroll-thin-dark mt-2 flex h-24 shrink-0 items-center gap-2 overflow-x-auto overflow-y-hidden py-1 sm:h-28"
                    >
                      {stageTiles.map((tile) => (
                        <div key={tile.identity} className="h-full w-36 shrink-0 sm:w-40">
                          <VideoTile
                            tile={tile}
                            isAdmin={presenceRoles.get(tile.identity) === 'admin'}
                            mirror={mirrorVideo && tile.isLocal}
                          />
                        </div>
                      ))}
                    </div>
                  ) : null}
                </div>
              ) : (
                <WebinarStage
                  shares={screenShares}
                  stageTiles={stageTiles}
                  roles={presenceRoles}
                  mirrorOwnVideo={mirrorVideo}
                />
              )}
              <ReactionOverlay floaters={floaters} />
              <AnimatePresence>
                {pill !== null ? (
                  <motion.div
                    key={pill.key}
                    initial={{ x: '-50%', y: -6, opacity: 0 }}
                    animate={{ x: '-50%', y: 0, opacity: 1 }}
                    exit={{ x: '-50%', y: -6, opacity: 0 }}
                    transition={{ duration: 0.18, ease: 'easeOut' }}
                    role="status"
                    data-testid="hotkey-pill"
                    className="pointer-events-none absolute top-4 left-1/2 z-30 flex -translate-x-1/2 select-none items-center gap-2 rounded-lg border border-white/10 bg-zinc-900/90 px-3 py-1.5 text-[12px] font-medium text-zinc-100 shadow-lg backdrop-blur-sm"
                  >
                    <PillIcon kind={pill.kind} />
                    {pill.text}
                  </motion.div>
                ) : null}
              </AnimatePresence>
            </div>
            <WebinarControlBar
              isHost={isHost}
              isAdmin={ownRole === 'admin'}
              boardOpen={boardBroadcastOpen}
              onToggleBoard={() => {
                realtimeActions.sendBoardSurface(!realtime.boardSurfaceOpen);
              }}
              micEnabled={room.localParticipant.isMicrophoneEnabled}
              camEnabled={room.localParticipant.isCameraEnabled}
              micLocked={publishLockedNow}
              camLocked={publishLockedNow}
              sharing={sharing}
              screenBusy={screenBusy}
              handRaised={handRaised}
              recording={recording}
              recBusy={recBusy}
              endingForAll={endingForAll}
              isMobile={isMobile}
              onToggleMic={() => {
                // Click-time re-guard through the refs: the handler closure may predate the
                // handover start (the lock prop renders a beat later), but the refs always
                // carry the current gate.
                if (!canPublishNowRef.current || mediaHandoverBusyRef.current) {
                  return;
                }
                const enabling = !room.localParticipant.isMicrophoneEnabled;
                void handleToggleMic();
                showPill(
                  t(enabling ? 'controls.pill_mic_on' : 'controls.pill_mic_off', { key: 'M' }),
                  enabling ? 'mic-on' : 'mic-off',
                );
              }}
              onToggleCam={() => {
                // Same re-guard as the microphone: the toggle hides while locked, and the
                // guard keeps a racing click (or a stale press) from publishing without a
                // grant. Staff and granted speakers pass - they hold publishing tokens.
                if (!canPublishNowRef.current || mediaHandoverBusyRef.current) {
                  return;
                }
                void handleToggleCam();
              }}
              onToggleShare={() => {
                // The share toggle renders for the same publish-capable audience as the
                // camera toggle; the start path re-checks nothing extra because the media
                // room behind roomRef is publish-capable whenever the toggle is visible.
                if (!canPublishNowRef.current || mediaHandoverBusyRef.current) {
                  return;
                }
                void handleShareScreen();
              }}
              onOpenEffects={() => {
                setEffectsOpen(true);
              }}
              quickSettings={quickSettings}
              onStartRec={() => {
                void handleRecordingStart();
              }}
              onStopRec={() => {
                void handleRecordingStop();
              }}
              onToggleHand={() => {
                const next = !handRaised;
                toggleHand();
                showPill(
                  t(next ? 'controls.pill_hand_up' : 'controls.pill_hand_down', { key: 'H' }),
                  'hand',
                );
              }}
              onSendReaction={(emoji) => {
                realtimeActions.sendReaction(emoji);
              }}
              onOpenPanel={() => {
                setWebinarPanelOpen(true);
              }}
              onLeave={handleLeave}
              onEndForAll={() => {
                void handleEndForAll();
              }}
            />
          </div>
          <WebinarSidePanel
            isMobile={isMobile}
            open={webinarPanelOpen}
            onClose={() => {
              setWebinarPanelOpen(false);
            }}
            ownIdentity={ownIdentity}
            chat={{
              messages: realtime.messages,
              disabled: realtime.health !== 'live',
              hasMore: realtime.hasMore,
              loadingOlder: realtime.loadingOlder,
              rateLimited: realtime.rateLimited,
              storageError: realtime.storageError,
              onLoadOlder: () => {
                void realtimeActions.loadOlder();
              },
              onSend: realtimeActions.sendChat,
            }}
            qa={{
              questions: realtime.questions,
              ownIdentity,
              canModerate: isHost,
              disabled: realtime.health !== 'live',
              notice:
                realtime.rateLimited === true
                  ? 'rate_limited'
                  : realtime.storageError === true
                    ? 'storage_error'
                    : null,
              ownUpvotes,
              onAsk: handleAsk,
              onUpvote: handleUpvote,
              onAnswer: handleAnswer,
              onDismiss: handleDismiss,
            }}
            viewers={{
              participants: realtime.participants,
              ownIdentity,
              isHost,
              grantedIds,
              busyIds: speakBusyIds,
              boardGrantedIds,
              quality: qualityMap,
              boardBusyIds,
              onGrantSpeak: (target) => {
                void handleGrantSpeak(target);
              },
              onRevokeSpeak: (target) => {
                void handleRevokeSpeak(target);
              },
              onGrantBoard: (target) => {
                void handleGrantBoard(target);
              },
              onRevokeBoard: (target) => {
                void handleRevokeBoard(target);
              },
            }}
          />
          {audioTracks.map((audio) => (
            <AudioSink key={audio.identity} track={audio.track} sinkId={speakerSinkId} />
          ))}
        </div>
      ) : connected ? (
        <div className="relative flex min-h-0 flex-1">
          <div className="relative flex min-w-0 flex-1 flex-col">
            <div
              className={cn(
                'relative flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto p-3 sm:p-4',
                // The control pill is fixed to the bottom edge on mobile; keep the stage clear.
                isMobile && 'pb-20',
              )}
            >
              {toast !== null ? (
                <div
                  role="alert"
                  data-testid="moderation-toast"
                  className="absolute left-1/2 top-3 z-30 flex -translate-x-1/2 items-center gap-2 rounded-full border border-amber-500/40 bg-amber-500/15 px-4 py-1.5 text-xs font-medium text-amber-200 backdrop-blur"
                >
                  <ShieldAlert className="h-3.5 w-3.5" />
                  {toast}
                </div>
              ) : null}
              {connPhase === 'reconnecting' ? (
                <p
                  role="status"
                  data-testid="reconnecting-banner"
                  className="absolute left-1/2 top-3 z-30 -translate-x-1/2 rounded-full border border-amber-500/40 bg-amber-500/15 px-4 py-1.5 text-xs font-medium text-amber-200 backdrop-blur"
                >
                  {t('room.reconnecting_banner')}
                </p>
              ) : null}
              {screenError !== null ? (
                <p role="alert" className="text-sm text-red-300">
                  {screenError}
                </p>
              ) : null}
              {controlError !== null ? (
                <p role="alert" className="text-sm text-red-300">
                  {controlError}
                </p>
              ) : null}
              {mediaWarnings.length > 0 ? (
                <ul className="list-disc rounded-lg border border-amber-500/30 bg-amber-500/10 px-6 py-2 text-xs text-amber-200">
                  {mediaWarnings.map((warning) => (
                    <li key={warning.kind}>
                      {t(`room.${warning.kind}_unavailable`, { detail: warning.detail })}
                    </li>
                  ))}
                </ul>
              ) : null}
              {/* The unified meeting layout: one dominant slot (pin > newest share > active
                  speaker with hysteresis > stable fallback) plus every other participant and
                  share as tiles in the fullscreen adaptive grid. */}
              <MeetingLayout
                tiles={tiles}
                shares={screenShares}
                activeSpeakers={activeSpeakers}
                pinnedKey={pinnedKey}
                isMobile={isMobile}
                mirrorOwnVideo={mirrorVideo}
                isAdminIdentity={isAdminIdentity}
                handRaisedFor={handRaisedFor}
                onTogglePin={togglePinFor}
              />
            </div>
            <ReactionOverlay floaters={floaters} />
            {/* Hotkey/toggle feedback pill (Syncra): auto-hides after 1.1s. */}
            <AnimatePresence>
              {pill !== null ? (
                <motion.div
                  key={pill.key}
                  initial={{ x: '-50%', y: -6, opacity: 0 }}
                  animate={{ x: '-50%', y: 0, opacity: 1 }}
                  exit={{ x: '-50%', y: -6, opacity: 0 }}
                  transition={{ duration: 0.18, ease: 'easeOut' }}
                  role="status"
                  data-testid="hotkey-pill"
                  className="pointer-events-none absolute top-4 left-1/2 z-30 flex -translate-x-1/2 select-none items-center gap-2 rounded-lg border border-white/10 bg-zinc-900/90 px-3 py-1.5 text-[12px] font-medium text-zinc-100 shadow-lg backdrop-blur-sm"
                >
                  <PillIcon kind={pill.kind} />
                  {pill.text}
                </motion.div>
              ) : null}
            </AnimatePresence>
            <ControlBar
              micEnabled={room.localParticipant.isMicrophoneEnabled}
              camEnabled={room.localParticipant.isCameraEnabled}
              sharing={sharing}
              screenBusy={screenBusy}
              panel={panel}
              boardOpen={boardOpen}
              isMobile={isMobile}
              handRaised={handRaised}
              reactionsOpen={reactionsOpen}
              unreadChat={unreadChat}
              participantsCount={tiles.length}
              lobbyPendingCount={realtime.lobbyPending.length}
              canRecordings={canModerate(ownRole)}
              recording={recording}
              recBusy={recBusy}
              onStartRec={() => {
                void handleRecordingStart();
              }}
              onStopRec={() => {
                void handleRecordingStop();
              }}
              canEndForAll={canModerate(ownRole)}
              endingForAll={endingForAll}
              isFullscreen={isFullscreen}
              onToggleHand={() => {
                const next = !handRaised;
                toggleHand();
                showPill(
                  t(next ? 'controls.pill_hand_up' : 'controls.pill_hand_down', { key: 'H' }),
                  'hand',
                );
              }}
              onToggleReactions={() => {
                setReactionsOpen((open) => !open);
              }}
              onSendReaction={(emoji) => {
                realtimeActions.sendReaction(emoji);
                setReactionsOpen(false);
              }}
              onToggleMic={() => {
                const enabling = !room.localParticipant.isMicrophoneEnabled;
                void handleToggleMic();
                showPill(
                  t(enabling ? 'controls.pill_mic_on' : 'controls.pill_mic_off', { key: 'M' }),
                  enabling ? 'mic-on' : 'mic-off',
                );
              }}
              onToggleCam={() => {
                const enabling = !room.localParticipant.isCameraEnabled;
                void handleToggleCam();
                showPill(
                  t(enabling ? 'controls.pill_cam_on' : 'controls.pill_cam_off', { key: 'V' }),
                  enabling ? 'cam-on' : 'cam-off',
                );
              }}
              onToggleShare={() => {
                void handleShareScreen();
              }}
              onTogglePanel={togglePanelFor}
              onToggleBoard={() => {
                setBoardOpen((open) => !open);
              }}
              onInvite={() => {
                setInviteOpen(true);
              }}
              onToggleFullscreen={handleToggleFullscreen}
              onOpenShortcuts={() => {
                setShortcutsOpen(true);
              }}
              onOpenEffects={() => {
                setEffectsOpen(true);
              }}
              quickSettings={quickSettings}
              onEndForAll={() => {
                void handleEndForAll();
              }}
              onLeave={handleLeave}
            />
          </div>
          {boardOpen ? (
            // The Доска overlay (ADR 0012): the collaborative canvas above the tile grid; the
            // stage keeps its audio and the control bar stays reachable underneath.
            <div
              data-testid="board-overlay"
              className="absolute inset-x-2 bottom-2 top-2 z-20 flex flex-col sm:inset-x-3"
            >
              <BoardPanel
                roomId={slug}
                isEditor={amBoardEditor}
                status={realtime.health}
                sendUpdate={sendBoardUpdate}
                subscribeSync={subscribeBoardSync}
                sendPointer={sendBoardPointer}
                subscribePointers={subscribeBoardPointers}
                labelFor={boardPointerLabelFor}
                variant="overlay"
                className="min-h-0 flex-1 shadow-2xl shadow-black/60"
                onClose={() => {
                  setBoardOpen(false);
                }}
              />
            </div>
          ) : null}
          {panel !== null ? (
            <SidePanel
              panel={panel}
              onPanelChange={togglePanelFor}
              onClose={() => {
                setPanel(null);
              }}
              isMobile={isMobile}
              tiles={tiles}
              sharingIdentities={sharingIdentities}
              pinnedIdentity={pinnedKey}
              quality={qualityMap}
              onTogglePin={(target) => {
                togglePinFor(target);
              }}
              ownIdentity={ownIdentity}
              presence={realtime.participants}
              ownRole={realtime.you?.role ?? 'participant'}
              onModerate={handleModerate}
              onSetRole={handleSetRole}
              onInvite={() => {
                setInviteOpen(true);
              }}
              onInvites={
                canModerate(realtime.you?.role ?? 'participant')
                  ? () => {
                      setInvitesOpen(true);
                    }
                  : undefined
              }
              lobbyPending={realtime.lobbyPending}
              onLobbyAdmit={realtimeActions.sendLobbyAdmit}
              onLobbyDeny={realtimeActions.sendLobbyDeny}
              recordings={
                canModerate(ownRole)
                  ? ({
                      room: slug,
                      getToken: () => renewableTokenRef.current,
                      refreshKey: recRefreshKey,
                      onError: showToast,
                    } satisfies RecordingsPanelProps)
                  : undefined
              }
              recentActions={
                canModerate(ownRole)
                  ? {
                      room: slug,
                      getToken: () => renewableTokenRef.current,
                      refreshKey: recentActionsKey,
                    }
                  : undefined
              }
              chat={{
                messages: realtime.messages,
                disabled: realtime.health !== 'live',
                hasMore: realtime.hasMore,
                loadingOlder: realtime.loadingOlder,
                rateLimited: realtime.rateLimited,
                storageError: realtime.storageError,
                onLoadOlder: () => {
                  void realtimeActions.loadOlder();
                },
                onSend: realtimeActions.sendChat,
              }}
            />
          ) : null}
          {audioTracks.map((audio) => (
            <AudioSink key={audio.identity} track={audio.track} sinkId={speakerSinkId} />
          ))}
        </div>
      ) : (
        <div className="flex flex-1 flex-col items-center overflow-y-auto px-4 py-8">
          <div className="mx-auto flex w-full max-w-4xl flex-col gap-4">
            {phase === 'joining' ? (
              <p role="status" className="text-sm text-zinc-400">
                {t('room.joining')}
              </p>
            ) : null}
            {phase === 'left' ? (
              <p role="status" className="text-sm text-emerald-300">
                {t('room.left')}
              </p>
            ) : null}
            {phase === 'dropped' ? (
              <div className="flex flex-col items-start gap-4 rounded-xl border border-red-500/30 bg-red-500/10 p-4">
                <p role="alert" data-testid="disconnect-reason" className="text-sm text-red-200">
                  {t('room.dropped', {
                    reason: t(`room.drop_reason_${dropReason ?? 'lost'}`),
                  })}
                </p>
                <button
                  type="button"
                  data-testid="rejoin"
                  onClick={handleRejoin}
                  className="rounded-full bg-zinc-800 px-4 py-2 text-sm font-medium text-zinc-100 transition-colors hover:bg-zinc-700"
                >
                  {t('room.back_to_prejoin')}
                </button>
              </div>
            ) : null}
            {phase === 'pre-join' || phase === 'left' || phase === 'failed' ? (
              <div className="flex flex-col gap-4">
                <a
                  href="/"
                  className="flex w-fit items-center gap-1.5 text-sm text-zinc-400 transition-colors hover:text-zinc-200"
                >
                  <ArrowLeft aria-hidden className="h-3.5 w-3.5" />
                  {t('room.back_home')}
                </a>
                {/* CR-26: the room lifecycle outcomes replace the prejoin card entirely - an
                    unknown room cannot be joined no matter what the device checks say, so the
                    screen offers the two ways forward (quick meeting / re-check the name), and
                    an expired one explains the deadline. */}
                {lifecycleState === 'room_not_found' ? (
                  <section
                    aria-label={t('room.not_found_title')}
                    data-testid="room-not-found"
                    className="rounded-2xl border border-white/[0.07] bg-zinc-900/60 p-6 shadow-2xl"
                  >
                    <h1 className="text-[19px] font-semibold tracking-tight text-white">
                      {t('room.not_found_title')}
                    </h1>
                    <p className="mt-2 max-w-prose text-sm text-zinc-400">
                      {t('room.not_found_description', { slug })}
                    </p>
                    <div className="mt-5 flex flex-wrap gap-2">
                      <button
                        type="button"
                        data-testid="not-found-create-quick"
                        onClick={() => {
                          navigateTo('/?action=create');
                        }}
                        className="h-10 rounded-xl bg-primary px-4 text-sm font-semibold text-primary-foreground transition-colors hover:bg-primary/90"
                      >
                        {t('room.not_found_create_quick')}
                      </button>
                      <button
                        type="button"
                        data-testid="not-found-check-name"
                        onClick={() => {
                          navigateTo('/?focus=join');
                        }}
                        className="h-10 rounded-xl border border-white/10 px-4 text-sm font-medium text-zinc-200 transition-colors hover:bg-white/5"
                      >
                        {t('room.not_found_check_name')}
                      </button>
                    </div>
                  </section>
                ) : lifecycleState === 'room_expired' ? (
                  <section
                    aria-label={t('room.expired_title')}
                    data-testid="room-expired"
                    className="rounded-2xl border border-white/[0.07] bg-zinc-900/60 p-6 shadow-2xl"
                  >
                    <h1 className="text-[19px] font-semibold tracking-tight text-white">
                      {t('room.expired_title')}
                    </h1>
                    <p className="mt-2 max-w-prose text-sm text-zinc-400">
                      {t('room.expired_description', { slug })}
                    </p>
                    <div className="mt-5 flex flex-wrap gap-2">
                      <button
                        type="button"
                        data-testid="expired-back-home"
                        onClick={() => {
                          navigateTo('/');
                        }}
                        className="h-10 rounded-xl border border-white/10 px-4 text-sm font-medium text-zinc-200 transition-colors hover:bg-white/5"
                      >
                        {t('room.expired_back_home')}
                      </button>
                    </div>
                  </section>
                ) : (
                  // Prejoin renders its own Verba card: room header, media area and join form.
                  <Prejoin
                    slug={slug}
                    joinError={error}
                    ssoNotice={ssoNotice}
                    onSsoNotice={setSsoNotice}
                    onJoin={handleJoin}
                  />
                )}
              </div>
            ) : null}
          </div>
        </div>
      )}
      {connected ? (
        <>
          {/* Syncra dialogs: the invite (with the copy-link affordance), the shortcuts and
              the effects surface (microphone processing + camera background). */}
          <InviteDialog open={inviteOpen} onOpenChange={setInviteOpen} link={link} code={slug} />
          {invitesOpen ? (
            <InvitesDialog
              open
              onOpenChange={setInvitesOpen}
              room={slug}
              getToken={() => renewableTokenRef.current}
            />
          ) : null}
          <ShortcutsDialog open={shortcutsOpen} onOpenChange={setShortcutsOpen} />
          <EffectsDialog
            open={effectsOpen}
            onOpenChange={setEffectsOpen}
            camera={{
              bgMode: storedPrefs.bgMode,
              bgQuality: storedPrefs.bgQuality,
              resolvedQuality: storedPrefs.resolvedQuality,
              blurStrength: storedPrefs.blurStrength,
              // In-call the camera choice applies LIVE: the store subscription drives the
              // replaceTrack machine against the published camera (no rejoin).
              onBgMode: (mode) => {
                devicePrefsStore().update({ bgMode: mode });
              },
              onBgQuality: (quality) => {
                devicePrefsStore().update({ bgQuality: quality });
              },
              onBlurStrength: (strength) => {
                devicePrefsStore().update({ blurStrength: strength });
              },
            }}
            filters={{
              filter: storedPrefs.videoFilter,
              onFilter: (filter) => {
                devicePrefsStore().update({ videoFilter: filter });
              },
            }}
            masks={{
              mask: storedPrefs.mask,
              onMask: (mask) => {
                devicePrefsStore().update({ mask });
              },
            }}
            microphone={{
              settings: micEffects,
              effective: micEffectsEffective,
              busy: micEffectsBusy,
              onToggle: (key, value) => {
                devicePrefsStore().update(prefsPatchForEffect(key, value));
              },
            }}
          />
        </>
      ) : null}
    </main>
  );
}

/** The icon of the hotkey feedback pill, colored per action like the mockup. */
function PillIcon({ kind }: { readonly kind: PillKind }): JSX.Element {
  switch (kind) {
    case 'mic-on':
      return <Mic className="size-3.5 text-emerald-400" />;
    case 'mic-off':
      return <MicOff className="size-3.5 text-red-400" />;
    case 'cam-on':
      return <Video className="size-3.5 text-emerald-400" />;
    case 'cam-off':
      return <VideoOff className="size-3.5 text-red-400" />;
    case 'hand':
      return <Hand className="size-3.5 text-amber-400" />;
  }
}

/**
 * Worst-of aggregate of the members' LiveKit connection qualities (the unified room header's
 * aggregate chip): one degraded member drags the room's reading down, since the chip speaks
 * for the whole call. An empty map (no quality event yet) reads as excellent.
 */
function aggregateQualityLevel(qualities: ReadonlyMap<string, string>): 1 | 2 | 3 {
  let worst: 1 | 2 | 3 = 3;
  for (const quality of qualities.values()) {
    const level = signalLevel(quality);
    if (level < worst) {
      worst = level;
    }
  }
  return worst;
}

/** Room-event callbacks the page derives its UI state from. */
interface RoomEventHandlers {
  readonly rerender: () => void;
  /** RoomEvent.ActiveSpeakersChanged: participants ordered loudest-first, local included. */
  readonly onActiveSpeakers: (speakers: ReadonlyArray<{ readonly identity: string }>) => void;
  /** RoomEvent.ConnectionStateChanged: raw ConnectionState string. */
  readonly onConnectionState: (state: string) => void;
  readonly onReconnecting: () => void;
  readonly onReconnected: () => void;
  /**
   * RoomEvent.ConnectionQualityChanged of ANY participant (the unified room header): the
   * local quality drives the own signal chip, every identity lands in the map the aggregate
   * chip and the people panels consume.
   */
  readonly onConnectionQuality: (
    quality: string,
    participant: { readonly identity: string; readonly isLocal: boolean },
  ) => void;
  /** Receipt of a server-side mute of the local microphone (moderator action). */
  readonly onRemotelyMuted: () => void;
  /** True while a local microphone toggle is in flight (its TrackMuted is not a moderator mute). */
  readonly isLocalMicActionPending: () => boolean;
  /** RoomEvent.Disconnected: the DisconnectReason (CLIENT_INITIATED for a local leave). */
  readonly onDisconnected: (reason: unknown) => void;
}

/** Wires every room event that changes the derived UI into a re-render or state update. */
function registerRoomEvents(room: Room, handlers: RoomEventHandlers): void {
  room.on(RoomEvent.ConnectionStateChanged, (state) => {
    handlers.onConnectionState(state);
  });
  room.on(RoomEvent.Connected, () => {
    handlers.onConnectionState('connected');
  });
  room.on(RoomEvent.Reconnecting, handlers.onReconnecting);
  room.on(RoomEvent.Reconnected, handlers.onReconnected);
  // Every member's connection quality feeds the aggregate chip and the people panels; the
  // local participant's quality alone drives the own signal chip.
  room.on(RoomEvent.ConnectionQualityChanged, (quality, participant) => {
    handlers.onConnectionQuality(String(quality), {
      identity: participant?.identity ?? '',
      isLocal: participant?.isLocal === true,
    });
  });
  room.on(RoomEvent.ActiveSpeakersChanged, (speakers) => {
    handlers.onActiveSpeakers(speakers);
    handlers.rerender();
  });
  room.on(RoomEvent.ParticipantConnected, handlers.rerender);
  room.on(RoomEvent.ParticipantDisconnected, handlers.rerender);
  room.on(RoomEvent.TrackSubscribed, handlers.rerender);
  room.on(RoomEvent.TrackUnsubscribed, handlers.rerender);
  // A TrackMuted on the local microphone track without a local toggle in flight is the
  // receipt of a server-side moderator mute (SFU enforcement reaches the publishing client).
  room.on(RoomEvent.TrackMuted, (publication, participant) => {
    if (
      participant?.isLocal === true &&
      publication?.source === Track.Source.Microphone &&
      !handlers.isLocalMicActionPending()
    ) {
      handlers.onRemotelyMuted();
    }
    handlers.rerender();
  });
  room.on(RoomEvent.TrackUnmuted, handlers.rerender);
  room.on(RoomEvent.LocalTrackPublished, handlers.rerender);
  // LocalTrackUnpublished also covers the SDK unpublishing a screen share after the
  // browser's native stop bar fired the track's `ended` event (2.x: LocalParticipant
  // wires TrackEvent.Ended -> unpublishTrack for screen shares) — the share button and
  // indicator flip back through this re-render.
  room.on(RoomEvent.LocalTrackUnpublished, handlers.rerender);
  room.on(RoomEvent.ParticipantMetadataChanged, handlers.rerender);
  room.on(RoomEvent.Disconnected, (reason) => {
    handlers.onDisconnected(reason);
  });
}

/**
 * Builds the connect options that carry the control-minted TURN entry as RTCIceServer config.
 * Setting iceServers replaces the server-provided list entirely — that is intended, because
 * LiveKit's own injected entry announces an unusable `turns:<domain>:443` behind the stand's
 * reverse proxy, and the control-minted TURN server doubles as STUN (SFU candidates still
 * arrive via signalling). Returns undefined when no TURN credentials were minted, leaving the
 * SDK's server-provided list in place (local stand).
 */
function turnConnectOptions(turn: TurnCredentials | undefined): RoomConnectOptions | undefined {
  if (turn === undefined) {
    return undefined;
  }
  return {
    rtcConfig: {
      iceServers: [
        {
          urls: [...turn.urls],
          username: turn.username,
          credential: turn.credential,
        },
      ],
    },
  };
}

/**
 * Connects the room, then publishes the camera and microphone picked on the prejoin screen,
 * each constrained to its selected deviceId. When the prejoin handed over a processed camera
 * (background blur/replacement), its track is published directly (publishTrack) instead of
 * capturing the device again; if that publish fails, the plain camera path is attempted as a
 * fallback. The camera publish options come from the decided publish path (H264/VP9/VP8 with
 * the session tier's primary resolution and per-layer bitrates; the capture is constrained to
 * the same tier resolution so a later raw<->canvas effects swap stays dimension-stable); the
 * microphone publishes with speech processing (echo cancellation, noise suppression, AGC) at
 * the speech bitrate. Publishing stays best-effort per kind: a device
 * that fails at join time must not kick the participant out. A kind the participant turned
 * off (null deviceId) is not attempted at all — including the microphone: the participant
 * unmutes from the control bar, which publishes the track then. A webinar viewer (viewer=true)
 * publishes nothing: their token is subscribe-only and a speak grant is the only way in
 * (reconnectMediaForSpeak).
 */
async function connectAndPublish(
  room: Room,
  url: string,
  token: string,
  connectOptions: RoomConnectOptions | undefined,
  media: PrejoinJoinRequest,
  publishDecision: PublishDecision,
  publishTier: PublishTier,
  background: BackgroundHandover | null,
  detailOf: (err: unknown) => string,
  viewer = false,
): Promise<readonly MediaWarning[]> {
  await room.connect(url, token, connectOptions);
  if (viewer) {
    return [];
  }
  const warnings: MediaWarning[] = [];
  if (media.cameraDeviceId !== null) {
    let processedPublished = false;
    if (background !== null && background.track.readyState === 'live') {
      try {
        await room.localParticipant.publishTrack(
          background.track,
          cameraPublishOptions(publishDecision, publishTier),
        );
        processedPublished = true;
      } catch {
        // The processed path is dead: release it and fall back to a plain capture below.
        // The room-level ref keeps pointing here; dispose is idempotent on later paths.
        background.dispose();
      }
    }
    if (!processedPublished) {
      try {
        await room.localParticipant.setCameraEnabled(
          true,
          cameraCaptureOptions(media.cameraDeviceId, publishTier),
          cameraPublishOptions(publishDecision, publishTier),
        );
      } catch (err: unknown) {
        warnings.push({ kind: 'camera', detail: detailOf(err) });
      }
    }
  }
  if (media.micDeviceId !== null) {
    try {
      await room.localParticipant.setMicrophoneEnabled(
        true,
        microphoneCaptureOptions(
          media.micDeviceId,
          audioEffectSettingsWithDefaults(
            media.micEchoCancellation,
            media.micNoiseSuppression,
            media.micAutoGainControl,
          ),
        ),
        microphonePublishOptions(),
      );
    } catch (err: unknown) {
      warnings.push({ kind: 'microphone', detail: detailOf(err) });
    }
  }
  return warnings;
}

function rawErrorMessage(err: unknown): string | null {
  if (err instanceof Error && err.message.length > 0) {
    return err.message;
  }
  return null;
}

/**
 * True when the browser picker was dismissed without choosing a source: getDisplayMedia
 * rejects with a NotAllowedError DOMException (the SDK rethrows it from
 * setScreenShareEnabled), which is a user decision rather than a failure.
 */
function isScreenShareCancellation(err: unknown): boolean {
  return err instanceof DOMException && err.name === 'NotAllowedError';
}
